import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Alert,
  Autocomplete,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControl,
  IconButton,
  InputAdornment,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Snackbar,
  Stack,
  Switch,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Tabs,
  TextField,
  Tooltip,
  Typography,
  Menu,
  MenuItem as MuiMenuItem,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import PauseCircleOutlineIcon from '@mui/icons-material/PauseCircleOutline';
import SearchIcon from '@mui/icons-material/Search';
import UploadFileIcon from '@mui/icons-material/UploadFile';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import DownloadIcon from '@mui/icons-material/Download';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import MapIcon from '@mui/icons-material/Map';
import PeopleIcon from '@mui/icons-material/People';
import LayersIcon from '@mui/icons-material/Layers';
import PendingActionsIcon from '@mui/icons-material/PendingActions';
import PlaceIcon from '@mui/icons-material/Place';
import RefreshIcon from '@mui/icons-material/Refresh';
import { useSearchParams, Link as RouterLink } from 'react-router-dom';
import paths from '@src/paths';

import api from '@src/api/clients';
import {
  createZipSubmission,
  cancelZipSubmission,
  checkZipOverlap,
  exportZipCoverageCsv,
  bulkCreateZipSubmissions,
  downloadZipTemplate,
  fetchZipSubmissions,
  fetchLatestByTerritory,
  pauseTerritory,
} from '@src/api/actions/dashboard/zipSubmissions';
import { formatDistanceToNow } from 'date-fns';
import { useAuth } from '@src/context';
import useClientAutocomplete from '@src/hooks/useClientAutocomplete';
import useParentCompanyAutocomplete from '@src/hooks/useParentCompanyAutocomplete';
import useLocalStorage from '@src/hooks/useLocalStorage';
import ClientZipMap from './components/ClientZipMap';
import SubmissionDiffMap from './components/SubmissionDiffMap';

const STATUS_CONFIG = {
  pending: { color: 'warning', label: 'Pending' },
  approved: { color: 'success', label: 'Approved' },
  rejected: { color: 'error', label: 'Rejected' },
  cancelled: { color: 'default', label: 'Cancelled' },
};

const WALKTHROUGH_STEPS = [
  {
    ref: 'addVerticalBtn',
    title: 'Add Verticals',
    description: 'If you are a new client or launching a new vertical, use this to add verticals to your account. Each vertical will appear as an option in your territories.',
  },
  {
    ref: 'newTerritoryBtn',
    title: 'Create Territories',
    description: 'Create territories to organize your ZIP coverage by region. Each territory can have multiple verticals with their own ZIPs.',
  },
  {
    ref: 'statsBar',
    title: 'Coverage at a Glance',
    description: 'See your total territories, ZIPs, active verticals, and pending submissions in one place.',
  },
  {
    ref: 'zipSearch',
    title: 'Find a ZIP',
    description: 'Quickly search to find which territory and vertical a specific ZIP code belongs to.',
  },
  {
    ref: 'territoryCard',
    title: 'Manage Your Territories',
    description: 'Expand a territory to see its verticals and ZIPs. Use "Manage ZIPs" to add or remove ZIPs, or the dropdown to add a new vertical.',
  },
  {
    ref: 'historyTab',
    title: 'Submission History',
    description: 'Track all your past submissions here — see what was approved, rejected, or is still pending review.',
  },
];

function WelcomeOverlay({ onStartTour, onSkip }) {
  return (
    <Dialog open fullWidth maxWidth="sm" PaperProps={{ sx: { borderRadius: 3, overflow: 'hidden' } }}>
      <Box sx={{ bgcolor: '#111827', color: '#fff', px: 4, pt: 4, pb: 3, textAlign: 'center' }}>
        <MapIcon sx={{ fontSize: 48, mb: 1, color: '#ff680d' }} />
        <Typography variant="h5" fontWeight={700}>
          Welcome to ZIP Coverage
        </Typography>
        <Typography variant="body2" sx={{ mt: 1, color: 'rgba(255,255,255,0.7)' }}>
          Manage your service areas, territories, and verticals all in one place.
        </Typography>
      </Box>
      <DialogContent sx={{ px: 4, py: 3 }}>
        <Typography variant="subtitle2" fontWeight={600} gutterBottom>
          Here's what you can do:
        </Typography>
        <Stack spacing={1.5} sx={{ mt: 1.5 }}>
          <StepRow number={1} text="Create territories to organize your coverage by region" />
          <StepRow number={2} text="Add verticals (service types) to each territory" />
          <StepRow number={3} text="Manage ZIP codes — add, remove, or replace in bulk" />
          <StepRow number={4} text="Track your submissions and see approval status" />
          <StepRow number={5} text="View your coverage on an interactive map" />
        </Stack>
      </DialogContent>
      <DialogActions sx={{ px: 4, pb: 3, justifyContent: 'center', gap: 2 }}>
        <Button variant="outlined" onClick={onSkip} sx={{ minWidth: 120 }}>
          Skip
        </Button>
        <Button variant="contained" onClick={onStartTour} sx={{ minWidth: 160, bgcolor: '#ff680d', '&:hover': { bgcolor: '#e55a00' } }}>
          Take a Quick Tour
        </Button>
      </DialogActions>
    </Dialog>
  );
}



function WalkthroughOverlay({ stepIndex, steps, stepRefs, onNext, onBack, onSkip }) {
  const step = steps[stepIndex];
  const ref = stepRefs[step?.ref];
  const el = ref?.current;
  const [rect, setRect] = useState(null);

  useEffect(() => {
    document.body.style.overflow = 'hidden';
    return () => { document.body.style.overflow = ''; };
  }, []);

  useEffect(() => {
    if (el) {
      el.scrollIntoView({ behavior: 'smooth', block: 'center' });
      const timer = setTimeout(() => {
        setRect(el.getBoundingClientRect());
      }, 350);
      return () => clearTimeout(timer);
    }
  }, [el, stepIndex]);

  if (!step || !el || !rect) return null;

  const isLast = stepIndex === steps.length - 1;
  const tooltipHeight = 180;
  const spaceBelow = window.innerHeight - rect.bottom - 16;
  const placeAbove = spaceBelow < tooltipHeight && rect.top > tooltipHeight;
  const tooltipTop = placeAbove ? rect.top - tooltipHeight - 16 : rect.bottom + 16;

  return (
    <>
      <Box
        onClick={onSkip}
        sx={{
          position: 'fixed',
          inset: 0,
          bgcolor: 'rgba(0,0,0,0.45)',
          zIndex: 1300,
          cursor: 'pointer',
        }}
      />
      <Box
        sx={{
          position: 'fixed',
          top: rect.top - 8,
          left: rect.left - 8,
          width: rect.width + 16,
          height: rect.height + 16,
          borderRadius: 2,
          border: '2px solid',
          borderColor: 'primary.main',
          boxShadow: '0 0 0 9999px rgba(0,0,0,0.45)',
          zIndex: 1301,
          pointerEvents: 'none',
        }}
      />
      <Paper
        elevation={8}
        sx={{
          position: 'fixed',
          top: tooltipTop,
          left: Math.max(16, Math.min(rect.left, window.innerWidth - 380)),
          width: 360,
          zIndex: 1302,
          p: 2.5,
          borderRadius: 2,
        }}
      >
        <Stack spacing={1.5}>
          <Stack direction="row" justifyContent="space-between" alignItems="center">
            <Typography variant="subtitle1" fontWeight={600}>{step.title}</Typography>
            <Typography color="text.secondary" variant="caption">
              {stepIndex + 1} / {steps.length}
            </Typography>
          </Stack>
          <Typography variant="body2" color="text.secondary">{step.description}</Typography>
          <Stack direction="row" justifyContent="space-between" spacing={1}>
            <Button size="small" variant="text" onClick={onSkip}>Skip tour</Button>
            <Stack direction="row" spacing={1}>
              {stepIndex > 0 && (
                <Button size="small" variant="text" onClick={onBack}>Back</Button>
              )}
              <Button size="small" variant="contained" onClick={onNext}>
                {isLast ? 'Done' : 'Next'}
              </Button>
            </Stack>
          </Stack>
        </Stack>
      </Paper>
    </>
  );
}

export default function ZipCoveragePage() {
  const [searchParams] = useSearchParams();
  const { state } = useAuth();
  const user = state?.user;
  const memberships = user?.client_memberships || [];
  const isStaff = Boolean(user?.is_staff || user?.is_superuser);

  // Detect parent-company-scoped membership
  const isParentScopeMember = !isStaff && memberships.length === 1
    && !memberships[0]?.client_id && Boolean(memberships[0]?.parent_company_id);
  const memberParentCompanyId = isParentScopeMember
    ? String(memberships[0].parent_company_id) : '';

  // Parent company filter (staff can pick any; parent-scope members are locked to theirs)
  const {
    options: parentCompanyOptions,
    findOptionByValue: findParentCompanyByValue,
  } = useParentCompanyAutocomplete({ skip: !isStaff && !isParentScopeMember });
  const [selectedParentCompanyId, setSelectedParentCompanyId] = useState(
    memberParentCompanyId || ''
  );

  // Client selection — filtered by parent company when set
  const {
    options: clientOptions,
    handleInputChange: handleClientSearch,
    findOptionByValue,
  } = useClientAutocomplete({
    parentCompanyId: selectedParentCompanyId || undefined,
  });

  const initialClientId = searchParams.get('client_id')
    || (memberships.length === 1 && memberships[0]?.client_id
      ? String(memberships[0].client_id)
      : '');
  const [selectedClientId, setSelectedClientId] = useState(initialClientId);

  // Walkthrough
  const [walkthroughSeen, setWalkthroughSeen] = useLocalStorage('zipCoverage.walkthroughSeen', false);
  const [welcomeOpen, setWelcomeOpen] = useState(false);
  const [walkthroughActive, setWalkthroughActive] = useState(false);
  const [walkthroughStep, setWalkthroughStep] = useState(0);
  const walkthroughRefs = {
    addVerticalBtn: useRef(null),
    newTerritoryBtn: useRef(null),
    statsBar: useRef(null),
    zipSearch: useRef(null),
    territoryCard: useRef(null),
    historyTab: useRef(null),
  };

  // Data
  const [territories, setTerritories] = useState([]);
  const [verticals, setVerticals] = useState([]);
  const [allVerticals, setAllVerticals] = useState([]);
  const [addedVerticalIds, setAddedVerticalIds] = useState(new Set());
  const [addVerticalDialogOpen, setAddVerticalDialogOpen] = useState(false);
  const [zipsByTerritoryVertical, setZipsByTerritoryVertical] = useState({});
  const [submissions, setSubmissions] = useState([]);
  const [rejectedSubmissions, setRejectedSubmissions] = useState([]);
  const [latestByTerritory, setLatestByTerritory] = useState([]);
  const [loading, setLoading] = useState(false);

  // Tabs & History
  const [activeTab, setActiveTab] = useState(0);
  const [historySubmissions, setHistorySubmissions] = useState([]);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [historyHasMore, setHistoryHasMore] = useState(false);
  const [historyPage, setHistoryPage] = useState(1);
  const [historyFilter, setHistoryFilter] = useState('all');

  // ZIP Search
  const [zipSearchText, setZipSearchText] = useState('');

  // Territory creation
  const [newTerritoryDialogOpen, setNewTerritoryDialogOpen] = useState(false);
  const [newTerritoryName, setNewTerritoryName] = useState('');
  const [newTerritoryError, setNewTerritoryError] = useState('');
  const [newTerritorySaving, setNewTerritorySaving] = useState(false);

  // Territory rename
  const [renameTerritoryDialogOpen, setRenameTerritoryDialogOpen] = useState(false);
  const [renameTerritory, setRenameTerritory] = useState(null);
  const [renameTerritoryName, setRenameTerritoryName] = useState('');
  const [renameTerritoryError, setRenameTerritoryError] = useState('');
  const [renameTerritorySaving, setRenameTerritorySaving] = useState(false);

  // Territory delete

  // Manage ZIPs dialog
  const [manageDialogOpen, setManageDialogOpen] = useState(false);
  const [manageTerritory, setManageTerritory] = useState(null);
  // Verticals being edited. Single-element array = legacy per-vertical edit;
  // multi-element = "applies to all of these verticals" (fan-out on submit).
  const [manageVerticals, setManageVerticals] = useState([]);
  const [manageZipText, setManageZipText] = useState('');
  const [manageMode, setManageMode] = useState('add'); // 'add' | 'replace' | 'remove'
  const [manageRemoveIds, setManageRemoveIds] = useState([]);
  const [manageSubmitting, setManageSubmitting] = useState(false);
  const [manageError, setManageError] = useState('');
  const [manageValidation, setManageValidation] = useState(null);
  const [manageValidating, setManageValidating] = useState(false);
  const [submitterNotes, setSubmitterNotes] = useState('');
  const [copyFromAnchorEl, setCopyFromAnchorEl] = useState(null);
  const [customizeAnchorEl, setCustomizeAnchorEl] = useState(null);
  const [historyMapOpenIds, setHistoryMapOpenIds] = useState(() => new Set());
  const [expandedCountyKeys, setExpandedCountyKeys] = useState(() => new Set());
  const [expandedVerticalCountiesKeys, setExpandedVerticalCountiesKeys] = useState(() => new Set());
  const [expandedTerritoryIds, setExpandedTerritoryIds] = useState(null);
  const [groupManageMenu, setGroupManageMenu] = useState(null); // { anchorEl, territory, verticals }
  const validateTimerRef = useRef(null);
  const fileInputRef = useRef(null);

  // More actions menu
  const [moreMenuAnchor, setMoreMenuAnchor] = useState(null);

  // Pause territory confirmation dialog
  const [pauseDialogOpen, setPauseDialogOpen] = useState(false);
  const [pauseTarget, setPauseTarget] = useState(null); // { territory, vertical }
  const [pauseSaving, setPauseSaving] = useState(false);

  // Cancel submission dialog
  const [cancelDialogOpen, setCancelDialogOpen] = useState(false);
  const [cancelSubmissionId, setCancelSubmissionId] = useState(null);
  const [cancelSaving, setCancelSaving] = useState(false);

  // Bulk upload dialog
  const [bulkUploadOpen, setBulkUploadOpen] = useState(false);
  const [bulkCsvText, setBulkCsvText] = useState('');
  const [bulkParsed, setBulkParsed] = useState(null);
  const [bulkSubmitting, setBulkSubmitting] = useState(false);
  const [bulkError, setBulkError] = useState('');
  const [bulkNotes, setBulkNotes] = useState('');
  const bulkFileInputRef = useRef(null);

  // Submission confirmation dialog
  const [confirmationDialog, setConfirmationDialog] = useState(null);

  // Snackbar
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

  const showSnack = (message, severity = 'success') =>
    setSnackbar({ open: true, message, severity });

  // ─── Data Loading ──────────────────────────────────────────────────

  const loadTerritories = useCallback(async () => {
    if (!selectedClientId) { setTerritories([]); return; }
    try {
      const res = await api.request({
        url: `dashboard/client-territories/?client_id=${selectedClientId}`,
        method: 'GET',
      });
      setTerritories(res.data || []);
    } catch { setTerritories([]); }
  }, [selectedClientId]);

  const loadVerticals = useCallback(async () => {
    if (!selectedClientId) { setVerticals([]); return; }
    try {
      const res = await api.request({
        url: `dashboard/client-zips/vertical_options/?client_id=${selectedClientId}`,
        method: 'GET',
      });
      setVerticals(res.data || []);
    } catch { setVerticals([]); }
  }, [selectedClientId]);

  const loadAllVerticals = useCallback(async () => {
    try {
      const res = await api.request({
        url: 'dashboard/verticals/all-options/',
        method: 'GET',
      });
      setAllVerticals(res.data || []);
    } catch { setAllVerticals([]); }
  }, []);

  const loadZips = useCallback(async () => {
    if (!selectedClientId) { setZipsByTerritoryVertical({}); return; }
    setLoading(true);
    try {
      const res = await api.request({
        url: `dashboard/client-zips/coverage_summary/?client_id=${selectedClientId}`,
        method: 'GET',
      });
      const grouped = {};
      (res.data || []).forEach((czv) => {
        const key = `${czv.client_territory_id || 'none'}__${czv.vertical_id}`;
        if (!grouped[key]) grouped[key] = [];
        grouped[key].push(czv);
      });
      setZipsByTerritoryVertical(grouped);
    } catch { setZipsByTerritoryVertical({}); }
    finally { setLoading(false); }
  }, [selectedClientId]);

  const loadSubmissions = useCallback(async () => {
    if (!selectedClientId) { setSubmissions([]); return; }
    try {
      const res = await fetchZipSubmissions({ client_id: selectedClientId, status: 'pending' });
      setSubmissions(res.data?.results || []);
    } catch { setSubmissions([]); }
  }, [selectedClientId]);

  const loadRejectedSubmissions = useCallback(async () => {
    if (!selectedClientId) { setRejectedSubmissions([]); return; }
    try {
      const res = await fetchZipSubmissions({ client_id: selectedClientId, status: 'rejected' });
      setRejectedSubmissions((res.data?.results || []).slice(0, 5));
    } catch { setRejectedSubmissions([]); }
  }, [selectedClientId]);

  const loadLatestByTerritory = useCallback(async () => {
    if (!selectedClientId) { setLatestByTerritory([]); return; }
    try {
      const res = await fetchLatestByTerritory(selectedClientId);
      setLatestByTerritory(res.data || []);
    } catch { setLatestByTerritory([]); }
  }, [selectedClientId]);

  const loadHistory = useCallback(async (page = 1, statusFilter = 'all', append = false) => {
    if (!selectedClientId) { setHistorySubmissions([]); return; }
    setHistoryLoading(true);
    try {
      const params = { client_id: selectedClientId, page, page_size: 25 };
      if (statusFilter !== 'all') params.status = statusFilter;
      const res = await fetchZipSubmissions(params);
      const results = res.data?.results || [];
      setHistorySubmissions((prev) => append ? [...prev, ...results] : results);
      setHistoryHasMore(!!res.data?.next);
      setHistoryPage(page);
    } catch { if (!append) setHistorySubmissions([]); }
    finally { setHistoryLoading(false); }
  }, [selectedClientId]);

  useEffect(() => { loadAllVerticals(); }, [loadAllVerticals]);

  useEffect(() => {
    setAddedVerticalIds(new Set());
    setActiveTab(0);
    setZipSearchText('');
    setHistoryFilter('all');
    loadTerritories();
    loadVerticals();
    loadZips();
    loadSubmissions();
    loadRejectedSubmissions();
    loadLatestByTerritory();
    setExpandedTerritoryIds(null);
  }, [selectedClientId]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (activeTab === 2 && selectedClientId) {
      setHistoryPage(1);
      loadHistory(1, historyFilter);
    }
  }, [activeTab, selectedClientId, historyFilter]); // eslint-disable-line react-hooks/exhaustive-deps

  // ─── Walkthrough ─────────────────────────────────────────────────

  useEffect(() => {
    if (!walkthroughSeen && !loading && selectedClientId && !walkthroughActive && !welcomeOpen) {
      const timer = setTimeout(() => {
        setWelcomeOpen(true);
      }, 400);
      return () => clearTimeout(timer);
    }
  }, [walkthroughSeen, loading, selectedClientId, walkthroughActive, welcomeOpen]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleWelcomeStartTour = useCallback(() => {
    setWelcomeOpen(false);
    const firstValid = WALKTHROUGH_STEPS.findIndex(
      (s) => walkthroughRefs[s.ref]?.current
    );
    if (firstValid >= 0) {
      setWalkthroughStep(firstValid);
      setWalkthroughActive(true);
    } else {
      setWalkthroughSeen(true);
    }
  }, [setWalkthroughSeen]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleWelcomeSkip = useCallback(() => {
    setWelcomeOpen(false);
    setWalkthroughSeen(true);
  }, [setWalkthroughSeen]);

  const endWalkthrough = useCallback(() => {
    setWalkthroughActive(false);
    setWalkthroughStep(0);
    setWalkthroughSeen(true);
  }, [setWalkthroughSeen]);

  const nextWalkthroughStep = useCallback(() => {
    let next = walkthroughStep + 1;
    // Skip steps whose ref element doesn't exist
    while (next < WALKTHROUGH_STEPS.length) {
      const ref = walkthroughRefs[WALKTHROUGH_STEPS[next].ref];
      if (ref?.current) break;
      next += 1;
    }
    if (next >= WALKTHROUGH_STEPS.length) {
      endWalkthrough();
    } else {
      setWalkthroughStep(next);
    }
  }, [endWalkthrough, walkthroughStep]); // eslint-disable-line react-hooks/exhaustive-deps

  const prevWalkthroughStep = useCallback(() => {
    let prev = walkthroughStep - 1;
    while (prev >= 0) {
      const ref = walkthroughRefs[WALKTHROUGH_STEPS[prev].ref];
      if (ref?.current) break;
      prev -= 1;
    }
    setWalkthroughStep(Math.max(0, prev));
  }, [walkthroughStep]); // eslint-disable-line react-hooks/exhaustive-deps

  // ─── Derived Data ──────────────────────────────────────────────────

  const availableVerticals = useMemo(() => {
    const clientIds = new Set(verticals.map((v) => v.id));
    const extra = allVerticals.filter(
      (v) => addedVerticalIds.has(v.id) && !clientIds.has(v.id)
    );
    return [...verticals, ...extra];
  }, [verticals, allVerticals, addedVerticalIds]);

  const verticalById = useMemo(() => {
    const map = {};
    allVerticals.forEach((v) => { map[v.id] = v; });
    availableVerticals.forEach((v) => { map[v.id] = v; });
    return map;
  }, [allVerticals, availableVerticals]);

  const territoryVerticals = useMemo(() => {
    const map = {};
    territories.forEach((t) => { map[t.id] = new Set(); });
    Object.keys(zipsByTerritoryVertical).forEach((key) => {
      const [tId, vId] = key.split('__');
      if (map[tId]) map[tId].add(Number(vId));
    });
    return map;
  }, [territories, zipsByTerritoryVertical]);

  const pendingByTerritoryVertical = useMemo(() => {
    const map = {};
    submissions.forEach((sub) => {
      const key = `${sub.client_territory || 'none'}__${sub.vertical}`;
      if (!map[key]) map[key] = [];
      map[key].push(sub);
    });
    return map;
  }, [submissions]);

  const territoriesWithPending = useMemo(() => {
    const set = new Set();
    submissions.forEach((sub) => {
      if (sub.client_territory) set.add(sub.client_territory);
    });
    return set;
  }, [submissions]);

  const latestByTerritoryMap = useMemo(() => {
    const map = {};
    (latestByTerritory || []).forEach((row) => {
      const tId = row.territory_id;
      if (!tId) return;
      const existing = map[tId];
      const when = row.reviewed_at ? new Date(row.reviewed_at) : null;
      if (!when) return;
      if (!existing || when > existing.when) {
        map[tId] = {
          when,
          reviewedBy: row.reviewed_by_email,
          submittedBy: row.submitted_by_email,
        };
      }
    });
    return map;
  }, [latestByTerritory]);

  const stats = useMemo(() => {
    const allZips = Object.values(zipsByTerritoryVertical);
    const totalZips = allZips.reduce((sum, arr) => sum + arr.length, 0);
    const standardZips = allZips.reduce(
      (sum, arr) => sum + arr.filter((z) => (z.zip_type || '').toUpperCase() === 'STANDARD').length,
      0,
    );
    const totalPopulation = allZips
      .reduce((sum, arr) => sum + arr.reduce((s, z) => s + (z.population || 0), 0), 0);
    const standardPopulation = allZips.reduce(
      (sum, arr) => sum + arr.reduce(
        (s, z) => s + ((z.zip_type || '').toUpperCase() === 'STANDARD' ? (z.population || 0) : 0),
        0,
      ),
      0,
    );
    const activeVerticalIds = new Set();
    Object.keys(zipsByTerritoryVertical).forEach((key) => {
      activeVerticalIds.add(key.split('__')[1]);
    });
    return {
      territories: territories.length,
      totalZips,
      standardZips,
      totalPopulation,
      standardPopulation,
      verticals: activeVerticalIds.size,
      pending: submissions.length,
    };
  }, [territories, zipsByTerritoryVertical, submissions]);

  const zipSearchResults = useMemo(() => {
    const q = zipSearchText.trim();
    if (!q || q.length < 2) return null;
    const results = [];
    for (const [key, zips] of Object.entries(zipsByTerritoryVertical)) {
      const [tId, vId] = key.split('__');
      const matching = zips.filter((czv) => czv.zip.includes(q));
      if (matching.length > 0) {
        const territory = territories.find((t) => String(t.id) === tId);
        const vertical = verticalById[Number(vId)];
        results.push({
          territory: territory?.name || 'Unknown',
          vertical: vertical?.vertical || 'Unknown',
          zips: matching.map((czv) => czv.zip),
        });
      }
    }
    return results;
  }, [zipSearchText, zipsByTerritoryVertical, territories, verticalById]);

  // ─── Territory Creation ────────────────────────────────────────────

  const handleCreateTerritory = async () => {
    const name = newTerritoryName.trim();
    if (!name) {
      setNewTerritoryError('Territory name is required.');
      return;
    }
    const existing = territories.find(
      (t) => t.name.toLowerCase() === name.toLowerCase()
    );
    if (existing) {
      setNewTerritoryError(`Territory "${existing.name}" already exists.`);
      return;
    }
    setNewTerritorySaving(true);
    setNewTerritoryError('');
    try {
      await api.request({
        url: 'dashboard/client-territories/',
        method: 'POST',
        payload: { client: selectedClientId, name },
      });
      setNewTerritoryDialogOpen(false);
      setNewTerritoryName('');
      loadTerritories();
      showSnack(`Territory "${name}" created.`);
    } catch (err) {
      const detail = err?.response?.data?.name?.[0]
        || err?.response?.data?.detail
        || 'Failed to create territory.';
      setNewTerritoryError(String(detail));
    } finally {
      setNewTerritorySaving(false);
    }
  };

  // ─── Territory Rename ──────────────────────────────────────────────

  const handleOpenRename = (territory) => {
    setRenameTerritory(territory);
    setRenameTerritoryName(territory.name);
    setRenameTerritoryError('');
    setRenameTerritoryDialogOpen(true);
  };

  const handleRenameTerritory = async () => {
    const name = renameTerritoryName.trim();
    if (!name) {
      setRenameTerritoryError('Territory name is required.');
      return;
    }
    if (name === renameTerritory.name) {
      setRenameTerritoryDialogOpen(false);
      return;
    }
    setRenameTerritorySaving(true);
    setRenameTerritoryError('');
    try {
      await api.request({
        url: `dashboard/client-territories/${renameTerritory.id}/`,
        method: 'PATCH',
        payload: { name },
      });
      setRenameTerritoryDialogOpen(false);
      loadTerritories();
      showSnack(`Territory renamed to "${name}".`);
    } catch (err) {
      const detail = err?.response?.data?.name?.[0]
        || err?.response?.data?.detail
        || 'Failed to rename territory.';
      setRenameTerritoryError(String(detail));
    } finally {
      setRenameTerritorySaving(false);
    }
  };


  // ─── Manage ZIPs ──────────────────────────────────────────────────

  // Pass a single vertical OR an array. Multi-vertical = fan-out on submit.
  const handleOpenManageDialog = (territory, verticalOrList) => {
    const list = Array.isArray(verticalOrList) ? verticalOrList : [verticalOrList];
    setManageTerritory(territory);
    setManageVerticals(list.filter(Boolean));
    setManageZipText('');
    setManageMode('add');
    setManageRemoveIds([]);
    setManageError('');
    setManageValidation(null);
    setManageValidating(false);
    setSubmitterNotes('');
    setCopyFromAnchorEl(null);
    setCustomizeAnchorEl(null);
    setManageDialogOpen(true);
  };

  const isMultiVerticalManage = manageVerticals.length > 1;
  // Anchor vertical drives "current ZIPs" display + overlap checks. For multi,
  // we use the first vertical (consolidated groups already share ZIP sets).
  const manageAnchorVertical = manageVerticals[0] || null;

  const currentManagedZips = useMemo(() => {
    if (!manageTerritory || !manageAnchorVertical) return [];
    const key = `${manageTerritory.id}__${manageAnchorVertical.id}`;
    return zipsByTerritoryVertical[key] || [];
  }, [manageTerritory, manageAnchorVertical, zipsByTerritoryVertical]);

  // Options for "Copy from another vertical" — any (territory, vertical) bucket
  // that isn't currently being edited and has at least one standard ZIP.
  const copySourceOptions = useMemo(() => {
    if (!manageTerritory || manageVerticals.length === 0) return [];
    const excludedKeys = new Set(
      manageVerticals.map((v) => `${manageTerritory.id}__${v.id}`),
    );
    const options = [];
    Object.entries(zipsByTerritoryVertical).forEach(([key, zips]) => {
      if (excludedKeys.has(key)) return;
      const [tId, vId] = key.split('__');
      const standardZips = zips.filter(
        (z) => (z.zip_type || '').toUpperCase() === 'STANDARD',
      );
      if (standardZips.length === 0) return;
      const territory = territories.find((t) => String(t.id) === tId);
      const vertical = verticalById[Number(vId)];
      if (!territory || !vertical) return;
      options.push({
        key,
        territoryName: territory.name,
        verticalName: vertical.vertical,
        zips: standardZips,
      });
    });
    options.sort(
      (a, b) => a.territoryName.localeCompare(b.territoryName)
        || a.verticalName.localeCompare(b.verticalName),
    );
    return options;
  }, [manageTerritory, manageVerticals, zipsByTerritoryVertical, territories, verticalById]);

  // Live validation for the manage dialog textarea
  useEffect(() => {
    if (!manageDialogOpen) {
      setManageValidation(null);
      setManageValidating(false);
      return;
    }

    const parsed = manageZipText
      .split(/[\n,\t]+/)
      .map((z) => {
        const t = z.trim();
        return /^\d{1,4}$/.test(t) ? t.padStart(5, '0') : t;
      })
      .filter(Boolean);

    if (parsed.length === 0) {
      setManageValidation(null);
      return;
    }

    const unique = [...new Set(parsed)];
    const activeSet = new Set(currentManagedZips.map((czv) => czv.zip));

    if (manageMode === 'remove') {
      // Remove mode: pasted ZIPs that match current become removes
      const matched = unique.filter((z) => activeSet.has(z));
      const notFound = unique.filter((z) => !activeSet.has(z));
      setManageValidation({
        total: unique.length,
        inputDupes: parsed.length - unique.length,
        matched,
        notFound,
        newZips: [],
        dupes: [],
        unknown: [],
        conflicts: [],
        apiChecked: true,
      });
      setManageValidating(false);
      return;
    }

    if (manageMode === 'replace') {
      // Replace mode: new ZIPs = adds, current ZIPs not in paste = removes
      const newZips = unique.filter((z) => !activeSet.has(z));
      const kept = unique.filter((z) => activeSet.has(z));
      const toRemove = currentManagedZips.filter((czv) => !unique.includes(czv.zip));
      setManageValidation({
        total: unique.length,
        inputDupes: parsed.length - unique.length,
        dupes: kept,
        newZips,
        toRemove,
        unknown: [],
        conflicts: [],
        apiChecked: false,
      });

      if (validateTimerRef.current) clearTimeout(validateTimerRef.current);
      if (newZips.length > 0 && manageTerritory && manageAnchorVertical) {
        setManageValidating(true);
        validateTimerRef.current = setTimeout(async () => {
          try {
            const res = await checkZipOverlap({
              client_id: Number(selectedClientId),
              vertical_id: manageAnchorVertical.id,
              client_territory_id: manageTerritory.id,
              zip_codes: newZips,
            });
            setManageValidation((prev) => prev ? {
              ...prev,
              unknown: res.data?.unknown_zips || [],
              conflicts: res.data?.conflicts || [],
              zipDetails: res.data?.zip_details || {},
              apiChecked: true,
            } : prev);
          } catch {
            // silent
          } finally {
            setManageValidating(false);
          }
        }, 800);
      } else {
        setManageValidation((prev) => prev ? { ...prev, apiChecked: true } : prev);
        setManageValidating(false);
      }
      return;
    }

    // Add mode (default)
    const dupes = unique.filter((z) => activeSet.has(z));
    const newZips = unique.filter((z) => !activeSet.has(z));

    setManageValidation({
      total: unique.length,
      inputDupes: parsed.length - unique.length,
      dupes,
      newZips,
      unknown: [],
      conflicts: [],
      apiChecked: false,
    });

    if (validateTimerRef.current) clearTimeout(validateTimerRef.current);

    if (newZips.length > 0 && manageTerritory && manageAnchorVertical) {
      setManageValidating(true);
      validateTimerRef.current = setTimeout(async () => {
        try {
          const res = await checkZipOverlap({
            client_id: Number(selectedClientId),
            vertical_id: manageAnchorVertical.id,
            client_territory_id: manageTerritory.id,
            zip_codes: newZips,
          });
          setManageValidation((prev) => prev ? {
            ...prev,
            unknown: res.data?.unknown_zips || [],
            conflicts: res.data?.conflicts || [],
            zipDetails: res.data?.zip_details || {},
            apiChecked: true,
          } : prev);
        } catch {
          // API check failed silently — submit will catch it
        } finally {
          setManageValidating(false);
        }
      }, 800);
    } else {
      setManageValidating(false);
    }

    return () => {
      if (validateTimerRef.current) clearTimeout(validateTimerRef.current);
    };
  }, [manageZipText, manageMode, manageDialogOpen, currentManagedZips, selectedClientId, manageTerritory, manageAnchorVertical]);

  // CSV file upload handler
  const handleFileUpload = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = (event) => {
      const text = event.target?.result || '';
      // Parse CSV: extract anything that looks like a ZIP (digits, allow leading zeros)
      const zips = text
        .split(/[\n\r,\t;|]+/)
        .map((s) => s.replace(/["']/g, '').trim())
        .filter((s) => /^\d{3,10}$/.test(s));
      if (zips.length === 0) {
        setManageError('No valid ZIP codes found in the file.');
        return;
      }
      setManageZipText((prev) => {
        const existing = prev.trim();
        return existing ? `${existing}\n${zips.join('\n')}` : zips.join('\n');
      });
      showSnack(`${zips.length} ZIP codes loaded from file.`);
    };
    reader.readAsText(file);
    // Reset so same file can be re-uploaded
    e.target.value = '';
  };

  const handleToggleRemove = (zipId) => {
    setManageRemoveIds((prev) =>
      prev.includes(zipId) ? prev.filter((id) => id !== zipId) : [...prev, zipId]
    );
  };

  const handleRemoveInvalidZips = () => {
    const badSet = new Set(manageValidation?.unknown || []);
    if (!badSet.size) return;
    const cleaned = manageZipText
      .split(/[\n,\t]+/)
      .map((z) => z.trim())
      .filter((z) => z && !badSet.has(z))
      .join('\n');
    setManageZipText(cleaned);
  };

  const handleSubmitZipChanges = async () => {
    const parsed = manageZipText
      .split(/[\n,\t]+/)
      .map((z) => {
        const t = z.trim();
        return /^\d{1,4}$/.test(t) ? t.padStart(5, '0') : t;
      })
      .filter(Boolean);
    const unique = [...new Set(parsed)];
    const activeSet = new Set(currentManagedZips.map((czv) => czv.zip));

    let addZips = [];
    let removeZips = [];

    if (manageMode === 'remove') {
      // Remove mode: pasted ZIPs that exist in current set become removes
      removeZips = unique.filter((z) => activeSet.has(z));
      if (!removeZips.length) {
        setManageError('None of the pasted ZIPs are currently active.');
        return;
      }
    } else if (manageMode === 'replace') {
      // Replace mode: pasted = desired final set
      addZips = unique.filter((z) => !activeSet.has(z));
      removeZips = currentManagedZips
        .filter((czv) => !unique.includes(czv.zip))
        .map((czv) => czv.zip);
      if (!addZips.length && !removeZips.length) {
        setManageError('The pasted ZIPs match the current set — nothing to change.');
        return;
      }
    } else {
      // Add mode: only new ZIPs (skip already active), chip-clicked = removes
      addZips = unique.filter((z) => !activeSet.has(z));
      removeZips = currentManagedZips
        .filter((czv) => manageRemoveIds.includes(czv.id))
        .map((czv) => czv.zip);
      if (!addZips.length && !removeZips.length) {
        setManageError('Add ZIPs or select ZIPs to remove.');
        return;
      }
    }

    if (addZips.length) {
      // For multi-vertical, run overlap checks per vertical so we surface
      // conflicts before round-tripping the create call.
      try {
        const checks = await Promise.all(
          manageVerticals.map((v) =>
            checkZipOverlap({
              client_id: Number(selectedClientId),
              vertical_id: v.id,
              client_territory_id: manageTerritory.id,
              zip_codes: addZips,
            }).then((res) => ({ vertical: v, data: res.data })),
          ),
        );
        const allUnknown = checks.flatMap((c) => c.data?.unknown_zips || []);
        if (allUnknown.length) {
          const uniq = [...new Set(allUnknown)];
          setManageError(`Unknown ZIP codes: ${uniq.slice(0, 10).join(', ')}`);
          return;
        }
        const blocked = checks.filter((c) => c.data?.has_conflicts);
        if (blocked.length) {
          const summary = blocked
            .slice(0, 3)
            .map((c) => {
              const detail = (c.data.conflicts || [])
                .slice(0, 3)
                .map((cf) => `${cf.zip} (in ${cf.current_territory})`)
                .join(', ');
              return manageVerticals.length > 1
                ? `${c.vertical.vertical}: ${detail}`
                : detail;
            })
            .join(' · ');
          setManageError(`ZIP conflicts with other territories: ${summary}`);
          return;
        }
      } catch {
        setManageError('Failed to check for overlaps.');
        return;
      }
    }

    const items = [
      ...addZips.map((z) => ({ zip_raw: z, action: 'add' })),
      ...removeZips.map((z) => ({ zip_raw: z, action: 'remove' })),
    ];

    setManageSubmitting(true);
    setManageError('');
    try {
      const verticalIds = manageVerticals.map((v) => v.id);
      const payload = {
        client_id: Number(selectedClientId),
        client_territory_id: manageTerritory.id,
        submitter_notes: submitterNotes.trim(),
        items,
      };
      if (verticalIds.length > 1) {
        payload.vertical_ids = verticalIds;
      } else {
        payload.vertical_id = verticalIds[0];
      }
      await createZipSubmission(payload);
      setManageDialogOpen(false);
      const verticalLabel = manageVerticals.length > 1
        ? `${manageVerticals.length} verticals`
        : manageVerticals[0]?.vertical || '';
      setConfirmationDialog({
        territory: manageTerritory.name,
        vertical: verticalLabel,
        addCount: addZips.length,
        removeCount: removeZips.length,
        submissionCount: manageVerticals.length > 1 ? manageVerticals.length : undefined,
      });
      loadSubmissions();
    } catch (err) {
      const data = err?.response?.data || {};
      let detail = data.detail
        || (data.conflicting_zips?.length
          ? `Pending conflicts: ${data.conflicting_zips.slice(0, 10).join(', ')}`
          : null)
        || (data.invalid_zips?.length
          ? `Unknown ZIP codes: ${data.invalid_zips.slice(0, 20).join(', ')}`
          : null)
        || 'Failed to submit changes.';
      if (data.verticals_blocked?.length) {
        const summary = data.verticals_blocked
          .slice(0, 3)
          .map((vb) => `${vb.vertical_name}: ${vb.detail || 'conflict'}`)
          .join(' · ');
        detail = `${detail} — ${summary}`;
      }
      setManageError(String(detail));
    } finally {
      setManageSubmitting(false);
    }
  };

  // ─── Bulk Upload ─────────────────────────────────────────────────

  const handleOpenBulkUpload = () => {
    setBulkCsvText('');
    setBulkParsed(null);
    setBulkError('');
    setBulkNotes('');
    setBulkSubmitting(false);
    setBulkUploadOpen(true);
  };

  const handleBulkFileUpload = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = (event) => {
      setBulkCsvText(event.target?.result || '');
    };
    reader.readAsText(file);
    e.target.value = '';
  };

  const parseBulkCsv = useCallback(() => {
    const lines = bulkCsvText.split(/\r?\n/).filter((l) => l.trim());
    if (lines.length < 2) {
      setBulkParsed(null);
      setBulkError(lines.length === 1 ? 'CSV needs a header row and at least one data row.' : '');
      return;
    }

    // Detect header
    const headerLine = lines[0];
    const sep = headerLine.includes('\t') ? '\t' : ',';
    const headers = headerLine.split(sep).map((h) => h.trim().toLowerCase().replace(/['"]/g, ''));
    const tIdx = headers.findIndex((h) => h === 'territory');
    const vIdx = headers.findIndex((h) => h === 'vertical');
    const zIdx = headers.findIndex((h) => h === 'zip' || h === 'zip_code' || h === 'zipcode');
    const aIdx = headers.findIndex((h) => h === 'action');

    if (tIdx === -1 || zIdx === -1) {
      setBulkError('CSV must have "Territory" and "ZIP" columns. "Vertical" and "Action" are optional.');
      setBulkParsed(null);
      return;
    }

    // Build lookup maps from current data
    const territoryMap = {};
    territories.forEach((t) => { territoryMap[t.name.toLowerCase()] = t; });
    const verticalMap = {};
    availableVerticals.forEach((v) => { verticalMap[v.vertical.toLowerCase()] = v; });

    const errors = [];
    const rows = [];

    for (let i = 1; i < lines.length; i++) {
      const cols = lines[i].split(sep).map((c) => c.trim().replace(/^["']|["']$/g, ''));
      const territoryName = cols[tIdx] || '';
      const verticalName = vIdx !== -1 ? (cols[vIdx] || '') : '';
      const rawZip = cols[zIdx] || '';
      const zip = /^\d{1,4}$/.test(rawZip) ? rawZip.padStart(5, '0') : rawZip;
      const action = aIdx !== -1 ? (cols[aIdx] || 'add').toLowerCase() : 'add';

      if (!zip) continue;

      // Match territory
      const tMatch = territoryMap[territoryName.toLowerCase()];
      if (!tMatch) {
        const validNames = Object.keys(territoryMap);
        const suggestions = validNames
          .filter((n) => n.includes(territoryName.toLowerCase()) || territoryName.toLowerCase().includes(n))
          .slice(0, 3)
          .map((n) => territoryMap[n].name);
        errors.push({ row: i + 1, field: 'Territory', value: territoryName, suggestions });
        continue;
      }

      // Match vertical (optional — if column missing, use first vertical for territory)
      let vMatch = null;
      if (verticalName) {
        vMatch = verticalMap[verticalName.toLowerCase()];
        if (!vMatch) {
          const validVNames = Object.keys(verticalMap);
          const suggestions = validVNames
            .filter((n) => n.includes(verticalName.toLowerCase()) || verticalName.toLowerCase().includes(n))
            .slice(0, 3)
            .map((n) => verticalMap[n].vertical);
          errors.push({ row: i + 1, field: 'Vertical', value: verticalName, suggestions });
          continue;
        }
      }

      rows.push({
        territory: tMatch,
        vertical: vMatch,
        zip,
        action: action === 'remove' ? 'remove' : 'add',
      });
    }

    // Group by territory + vertical
    const groupKey = (r) => `${r.territory.id}__${r.vertical?.id || 'auto'}`;
    const groups = {};
    rows.forEach((r) => {
      const key = groupKey(r);
      if (!groups[key]) {
        groups[key] = {
          territory: r.territory,
          vertical: r.vertical,
          adds: [],
          removes: [],
        };
      }
      if (r.action === 'remove') {
        groups[key].removes.push(r.zip);
      } else {
        groups[key].adds.push(r.zip);
      }
    });

    setBulkParsed({
      groups: Object.values(groups),
      errors,
      totalRows: rows.length,
    });
    setBulkError(errors.length > 0 ? `${errors.length} row${errors.length > 1 ? 's' : ''} have unmatched territory/vertical names.` : '');
  }, [bulkCsvText, territories, availableVerticals]);

  useEffect(() => {
    if (bulkUploadOpen && bulkCsvText.trim()) {
      parseBulkCsv();
    } else if (bulkUploadOpen) {
      setBulkParsed(null);
      setBulkError('');
    }
  }, [bulkCsvText, bulkUploadOpen, parseBulkCsv]);

  const handleBulkSubmit = async () => {
    if (!bulkParsed?.groups?.length || bulkParsed.errors.length > 0) return;

    const items = [];
    bulkParsed.groups.forEach((g) => {
      g.adds.forEach((z) => items.push({
        territory_name: g.territory.name,
        vertical_name: g.vertical?.vertical || '',
        zip_raw: z,
        action: 'add',
      }));
      g.removes.forEach((z) => items.push({
        territory_name: g.territory.name,
        vertical_name: g.vertical?.vertical || '',
        zip_raw: z,
        action: 'remove',
      }));
    });

    setBulkSubmitting(true);
    setBulkError('');
    try {
      const res = await bulkCreateZipSubmissions({
        client_id: Number(selectedClientId),
        submitter_notes: bulkNotes.trim(),
        items,
      });
      setBulkUploadOpen(false);
      const subs = res.data || [];
      const totalAdds = bulkParsed.groups.reduce((s, g) => s + g.adds.length, 0);
      const totalRemoves = bulkParsed.groups.reduce((s, g) => s + g.removes.length, 0);
      setConfirmationDialog({
        territory: `${bulkParsed.groups.length} territories`,
        vertical: 'Bulk Upload',
        addCount: totalAdds,
        removeCount: totalRemoves,
        submissionCount: subs.length,
      });
      loadSubmissions();
    } catch (err) {
      const data = err?.response?.data || {};
      if (data.unmatched_territories?.length || data.unmatched_verticals?.length) {
        const parts = [];
        (data.unmatched_territories || []).forEach((t) => {
          parts.push(`Territory "${t.name}"${t.suggestions?.length ? ` — did you mean: ${t.suggestions.join(', ')}?` : ' not found'}`);
        });
        (data.unmatched_verticals || []).forEach((v) => {
          parts.push(`Vertical "${v.name}"${v.suggestions?.length ? ` — did you mean: ${v.suggestions.join(', ')}?` : ' not found'}`);
        });
        setBulkError(parts.join('\n'));
      } else {
        setBulkError(data.detail || 'Failed to submit bulk upload.');
      }
    } finally {
      setBulkSubmitting(false);
    }
  };

  const handleDownloadTemplate = async () => {
    try {
      const res = await downloadZipTemplate(selectedClientId);
      const blob = new Blob([res.data], { type: 'text/csv' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `zip_upload_template_${selectedClientId}.csv`;
      a.click();
      URL.revokeObjectURL(url);
    } catch {
      showSnack('Failed to download template.', 'error');
    }
  };

  // ─── Pause Territory ──────────────────────────────────────────────

  const handlePauseTerritory = (territory, vertical = null) => {
    setPauseTarget({ territory, vertical });
    setPauseDialogOpen(true);
  };

  const handlePauseConfirm = async () => {
    if (!pauseTarget) return;
    const { territory, vertical } = pauseTarget;
    const label = vertical
      ? `${territory.name} / ${vertical.vertical}`
      : territory.name;
    setPauseSaving(true);
    try {
      const payload = {
        client_id: Number(selectedClientId),
        client_territory_id: territory.id,
      };
      if (vertical) payload.vertical_id = vertical.id;
      await pauseTerritory(payload);
      showSnack(`Pause request submitted for ${label}.`);
      loadSubmissions();
      setPauseDialogOpen(false);
      setPauseTarget(null);
    } catch (err) {
      const detail = err?.response?.data?.detail || 'Failed to submit pause request.';
      showSnack(detail, 'error');
    } finally {
      setPauseSaving(false);
    }
  };

  // ─── Cancel Submission ─────────────────────────────────────────────

  const handleCancelSubmissionOpen = (submissionId) => {
    setCancelSubmissionId(submissionId);
    setCancelDialogOpen(true);
  };

  const handleCancelSubmissionConfirm = async () => {
    setCancelSaving(true);
    try {
      await cancelZipSubmission(cancelSubmissionId);
      setCancelDialogOpen(false);
      showSnack('Submission cancelled.');
      loadSubmissions();
    } catch {
      showSnack('Failed to cancel submission.', 'error');
    } finally {
      setCancelSaving(false);
    }
  };

  // ─── Render ────────────────────────────────────────────────────────

  const showClientSelector = isStaff || memberships.length > 1 || isParentScopeMember;

  return (
    <Box p={3}>
      {/* ─── Header ──────────────────────────────────────────────────── */}
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
        <Typography variant="h4" fontWeight={700}>ZIP Coverage</Typography>
        <Stack direction="row" spacing={1} alignItems="center">
          <Button
            ref={walkthroughRefs.newTerritoryBtn}
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => {
              setNewTerritoryName('');
              setNewTerritoryError('');
              setNewTerritoryDialogOpen(true);
            }}
            disabled={!selectedClientId}
          >
            New Territory
          </Button>
          <Button
            variant="outlined"
            startIcon={<UploadFileIcon />}
            onClick={handleOpenBulkUpload}
            disabled={!selectedClientId}
          >
            Bulk Upload
          </Button>
          <IconButton
            size="small"
            onClick={(e) => setMoreMenuAnchor(e.currentTarget)}
          >
            <MoreVertIcon />
          </IconButton>
          <Menu
            anchorEl={moreMenuAnchor}
            open={Boolean(moreMenuAnchor)}
            onClose={() => setMoreMenuAnchor(null)}
          >
            <MuiMenuItem
              ref={walkthroughRefs.addVerticalBtn}
              onClick={() => { setAddVerticalDialogOpen(true); setMoreMenuAnchor(null); }}
              disabled={!selectedClientId}
            >
              <AddIcon fontSize="small" sx={{ mr: 1 }} /> Add Vertical
            </MuiMenuItem>
            <MuiMenuItem
              onClick={() => {
                loadTerritories(); loadVerticals(); loadZips(); loadSubmissions(); loadRejectedSubmissions();
                setMoreMenuAnchor(null);
              }}
              disabled={loading || !selectedClientId}
            >
              <RefreshIcon fontSize="small" sx={{ mr: 1 }} /> Refresh
            </MuiMenuItem>
            <MuiMenuItem
              onClick={() => { setWalkthroughStep(0); setWalkthroughActive(true); setMoreMenuAnchor(null); }}
            >
              <HelpOutlineIcon fontSize="small" sx={{ mr: 1 }} /> Take a Tour
            </MuiMenuItem>
          </Menu>
        </Stack>
      </Box>

      {showClientSelector && (
        <Stack direction="row" spacing={2} mb={2} alignItems="center" flexWrap="wrap">
          {(isStaff || isParentScopeMember) && parentCompanyOptions.length > 0 && (
            <Autocomplete
              options={parentCompanyOptions}
              getOptionLabel={(o) => o.label}
              isOptionEqualToValue={(o, v) => o.value === v.value}
              value={findParentCompanyByValue(selectedParentCompanyId)}
              onChange={(_, val) => {
                setSelectedParentCompanyId(val ? String(val.value) : '');
                setSelectedClientId('');
              }}
              disabled={isParentScopeMember}
              renderInput={(params) => (
                <TextField {...params} label="Parent Company" size="small" sx={{ minWidth: 240 }} />
              )}
              sx={{ minWidth: 240 }}
            />
          )}
          <Autocomplete
            options={clientOptions}
            getOptionLabel={(o) => o.label}
            isOptionEqualToValue={(o, v) => o.value === v.value}
            value={findOptionByValue(selectedClientId)}
            onChange={(_, val) => setSelectedClientId(val ? String(val.value) : '')}
            onInputChange={handleClientSearch}
            renderInput={(params) => (
              <TextField {...params} label="Client" size="small" sx={{ minWidth: 280 }} />
            )}
            sx={{ minWidth: 280 }}
          />
        </Stack>
      )}

      {!selectedClientId && (
        <Alert severity="info">Select a client to view ZIP coverage.</Alert>
      )}

      {selectedClientId && loading && (
        <Box display="flex" justifyContent="center" my={4}>
          <CircularProgress />
        </Box>
      )}

      {/* ─── Quick Stats ──────────────────────────────────────────────── */}
      {selectedClientId && !loading && (
        <Box ref={walkthroughRefs.statsBar} display="flex" gap={2} mb={2} flexWrap="wrap" alignItems="center">
          <StatCard label="Territories" value={stats.territories} />
          <StatCard
            label="Active ZIPs"
            value={stats.standardZips.toLocaleString()}
            subtitle={
              stats.totalZips !== stats.standardZips
                ? `+ ${(stats.totalZips - stats.standardZips).toLocaleString()} PO Box/Unique (can't be targeted on Meta)`
                : null
            }
          />
          {stats.standardPopulation > 0 && (
            <StatCard
              label="Population"
              value={stats.standardPopulation.toLocaleString()}
              subtitle={
                stats.totalPopulation !== stats.standardPopulation
                  ? `from targetable ZIPs only`
                  : null
              }
            />
          )}
          <StatCard label="Verticals" value={stats.verticals} />
          <StatCard
            label="Pending"
            value={stats.pending}
            highlight={stats.pending > 0}
          />
          {stats.totalZips > 0 && (
            <Tooltip title="Download ZIP coverage as CSV">
              <IconButton
                size="small"
                onClick={async () => {
                  try {
                    const res = await exportZipCoverageCsv(selectedClientId);
                    const blob = new Blob([res.data], { type: 'text/csv' });
                    const url = URL.createObjectURL(blob);
                    const a = document.createElement('a');
                    a.href = url;
                    a.download = `zip_coverage_${selectedClientId}.csv`;
                    a.click();
                    URL.revokeObjectURL(url);
                    showSnack('CSV downloaded.');
                  } catch {
                    showSnack('Failed to download CSV.', 'error');
                  }
                }}
              >
                <DownloadIcon />
              </IconButton>
            </Tooltip>
          )}
        </Box>
      )}

      {/* ─── ZIP Search ───────────────────────────────────────────────── */}
      {selectedClientId && !loading && stats.totalZips > 0 && (
        <Box ref={walkthroughRefs.zipSearch} mb={2}>
          <TextField
            size="small"
            placeholder="Search for a ZIP code..."
            value={zipSearchText}
            onChange={(e) => setZipSearchText(e.target.value)}
            sx={{ width: 300 }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon fontSize="small" color="action" />
                </InputAdornment>
              ),
            }}
          />
          {zipSearchResults !== null && (
            <Paper variant="outlined" sx={{ mt: 1, p: 1.5, maxWidth: 500 }}>
              {zipSearchResults.length === 0 ? (
                <Typography variant="body2" color="text.secondary">
                  No matching ZIPs found.
                </Typography>
              ) : (
                <Stack spacing={1}>
                  {zipSearchResults.map((r, i) => (
                    <Box key={i}>
                      <Typography variant="body2" fontWeight={600}>
                        {r.territory} / {r.vertical}
                      </Typography>
                      <Box display="flex" flexWrap="wrap" gap={0.5} mt={0.5}>
                        {r.zips.slice(0, 20).map((z) => (
                          <Chip key={z} label={z} size="small" variant="outlined" />
                        ))}
                        {r.zips.length > 20 && (
                          <Chip label={`+${r.zips.length - 20} more`} size="small" />
                        )}
                      </Box>
                    </Box>
                  ))}
                </Stack>
              )}
            </Paper>
          )}
        </Box>
      )}

      {/* ─── Pending Submissions Banner (visible on all tabs) ──────────── */}
      {selectedClientId && !loading && submissions.length > 0 && (
        <Alert
          severity="warning"
          icon={<PendingActionsIcon fontSize="inherit" />}
          sx={{ mb: 2, alignItems: 'center' }}
          action={
            <Stack direction="row" spacing={1}>
              <Button
                size="small"
                variant="contained"
                color="warning"
                component={RouterLink}
                to={`${paths.zipSubmissions}?client_id=${selectedClientId}&status=pending`}
              >
                Review
              </Button>
            </Stack>
          }
        >
          <Typography variant="body2" fontWeight={600} sx={{ mb: 0.25 }}>
            {submissions.length} submission{submissions.length > 1 ? 's' : ''} awaiting review
          </Typography>
          <Typography variant="caption" color="text.secondary">
            {submissions
              .slice(0, 3)
              .map((sub) => {
                const t = territories.find((x) => x.id === sub.client_territory)?.name
                  || sub.territory_name
                  || 'Unknown territory';
                const v = sub.vertical_name || verticalById[sub.vertical]?.vertical || 'Vertical';
                const adds = (sub.items || []).filter((i) => i.action === 'add').length;
                const removes = (sub.items || []).filter((i) => i.action === 'remove').length;
                const delta = [adds ? `+${adds}` : null, removes ? `−${removes}` : null]
                  .filter(Boolean)
                  .join(' / ');
                return `${t} · ${v}${delta ? ` (${delta} ZIPs)` : ''}`;
              })
              .join(' · ')}
            {submissions.length > 3 ? ` · +${submissions.length - 3} more` : ''}
          </Typography>
        </Alert>
      )}

      {/* ─── Tabs ─────────────────────────────────────────────────────── */}
      {selectedClientId && !loading && (
        <Tabs
          ref={walkthroughRefs.historyTab}
          value={activeTab}
          onChange={(_, v) => setActiveTab(v)}
          sx={{ mb: 2, borderBottom: 1, borderColor: 'divider' }}
        >
          <Tab label="Coverage" />
          <Tab label="Map" />
          <Tab label="History" />
        </Tabs>
      )}

      {/* ═══════════ TAB 0: Coverage ═══════════ */}
      {selectedClientId && !loading && activeTab === 0 && (
        <>
          {/* Guided empty state */}
          {territories.length === 0 && (
            <Paper
              variant="outlined"
              sx={{
                p: 5,
                textAlign: 'center',
                mt: 2,
                borderStyle: 'dashed',
                borderColor: 'divider',
                bgcolor: 'background.default',
              }}
            >
              <MapIcon sx={{ fontSize: 64, color: 'action.disabled', mb: 2 }} />
              <Typography variant="h5" fontWeight={600} gutterBottom>
                Set up your ZIP coverage
              </Typography>
              <Typography variant="body1" color="text.secondary" sx={{ mb: 3, maxWidth: 480, mx: 'auto' }}>
                Define the territories and ZIP codes where you want to receive leads.
                All changes go through a quick review before going live.
              </Typography>
              <Stack spacing={2} alignItems="center" sx={{ maxWidth: 420, mx: 'auto', mb: 3 }}>
                <StepRow number={1} text='Click "New Territory" above to create your first territory' />
                <StepRow number={2} text="Select which verticals (service types) you offer" />
                <StepRow number={3} text="Add your ZIP codes — paste them in or upload a CSV" />
              </Stack>
              <Button
                variant="contained"
                size="large"
                startIcon={<AddIcon />}
                onClick={() => {
                  setNewTerritoryName('');
                  setNewTerritoryError('');
                  setNewTerritoryDialogOpen(true);
                }}
              >
                Create Your First Territory
              </Button>
            </Paper>
          )}


          {/* Recently Rejected Submissions — with ZIP chips */}
          {rejectedSubmissions.length > 0 && (
            <Box mb={2}>
              {rejectedSubmissions.map((sub) => {
                const adds = (sub.items || []).filter((i) => i.action === 'add');
                const removes = (sub.items || []).filter((i) => i.action === 'remove');
                return (
                  <Alert key={sub.id} severity="error" sx={{ mb: 1 }}>
                    <Typography variant="body2" fontWeight={600} sx={{ mb: 0.5 }}>
                      Rejected: {sub.territory_name} / {sub.vertical_name}
                    </Typography>
                    {adds.length > 0 && (
                      <Box display="flex" flexWrap="wrap" gap={0.5} alignItems="center" mb={0.5}>
                        <Typography variant="caption" color="text.secondary" sx={{ mr: 0.5 }}>
                          Adding:
                        </Typography>
                        {adds.slice(0, 20).map((item) => (
                          <Chip
                            key={item.id}
                            label={item.zip || item.zip_raw}
                            size="small"
                            color="error"
                            variant="outlined"
                            sx={{ height: 20, fontSize: '0.7rem' }}
                          />
                        ))}
                        {adds.length > 20 && (
                          <Chip label={`+${adds.length - 20} more`} size="small" sx={{ height: 20, fontSize: '0.7rem' }} />
                        )}
                      </Box>
                    )}
                    {removes.length > 0 && (
                      <Box display="flex" flexWrap="wrap" gap={0.5} alignItems="center" mb={0.5}>
                        <Typography variant="caption" color="text.secondary" sx={{ mr: 0.5 }}>
                          Removing:
                        </Typography>
                        {removes.slice(0, 20).map((item) => (
                          <Chip
                            key={item.id}
                            label={item.zip || item.zip_raw}
                            size="small"
                            color="error"
                            variant="outlined"
                            sx={{ height: 20, fontSize: '0.7rem' }}
                          />
                        ))}
                        {removes.length > 20 && (
                          <Chip label={`+${removes.length - 20} more`} size="small" sx={{ height: 20, fontSize: '0.7rem' }} />
                        )}
                      </Box>
                    )}
                    {sub.reviewer_notes && (
                      <Typography variant="body2" sx={{ mt: 0.5 }}>
                        Note: {sub.reviewer_notes}
                      </Typography>
                    )}
                  </Alert>
                );
              })}
            </Box>
          )}

          {/* Expand/Collapse all — only when there are enough territories to matter */}
          {territories.length > 3 && (
            <Box display="flex" justifyContent="flex-end" gap={1} mb={1}>
              <Button
                size="small"
                variant="text"
                onClick={() => setExpandedTerritoryIds(new Set(territories.map((t) => t.id)))}
              >
                Expand all
              </Button>
              <Button
                size="small"
                variant="text"
                onClick={() => setExpandedTerritoryIds(new Set())}
              >
                Collapse all
              </Button>
            </Box>
          )}

          {/* Territory Cards */}
          {territories.map((territory, tIdx) => {
            const tVerticalIds = territoryVerticals[territory.id] || new Set();
            const pendingVerticalIds = new Set();
            submissions
              .filter((s) => s.client_territory === territory.id)
              .forEach((s) => pendingVerticalIds.add(s.vertical));
            const usedVerticalIds = new Set([...tVerticalIds, ...pendingVerticalIds]);
            const displayVerticals = [...usedVerticalIds]
              .map((vId) => verticalById[vId])
              .filter(Boolean);
            const hasPending = territoriesWithPending.has(territory.id);
            const territoryKeys = Object.keys(zipsByTerritoryVertical)
              .filter((k) => k.startsWith(`${territory.id}__`));
            const territoryZipCount = territoryKeys
              .reduce((sum, k) => sum + zipsByTerritoryVertical[k].length, 0);
            const territoryStandardZipCount = territoryKeys
              .reduce(
                (sum, k) => sum + zipsByTerritoryVertical[k].filter(
                  (z) => (z.zip_type || '').toUpperCase() === 'STANDARD',
                ).length,
                0,
              );
            const territoryPopulation = territoryKeys
              .reduce((sum, k) => sum + zipsByTerritoryVertical[k].reduce((s, z) => s + (z.population || 0), 0), 0);

            const TERRITORY_COLORS = ['#1976d2', '#2e7d32', '#ed6c02', '#9c27b0', '#0288d1', '#d32f2f', '#689f38', '#5d4037'];
            const isPaused = territoryZipCount === 0 && !hasPending;
            const borderColor = hasPending ? '#ed6c02' : isPaused ? '#9e9e9e' : TERRITORY_COLORS[tIdx % TERRITORY_COLORS.length];

            const defaultExpanded = territories.length <= 10 && !isPaused;
            const expanded = expandedTerritoryIds == null
              ? defaultExpanded
              : expandedTerritoryIds.has(territory.id);

            return (
              <Accordion
                key={territory.id}
                ref={tIdx === 0 ? walkthroughRefs.territoryCard : undefined}
                expanded={expanded}
                onChange={(_, isExpanded) => {
                  setExpandedTerritoryIds((prev) => {
                    // Initialize from current default state on first interaction
                    const base = prev == null
                      ? new Set(
                        territories
                          .filter((t) => {
                            const tKeys = Object.keys(zipsByTerritoryVertical)
                              .filter((k) => k.startsWith(`${t.id}__`));
                            const tCount = tKeys
                              .reduce((sum, k) => sum + zipsByTerritoryVertical[k].length, 0);
                            const tPaused = tCount === 0
                              && !territoriesWithPending.has(t.id);
                            return territories.length <= 10 && !tPaused;
                          })
                          .map((t) => t.id),
                      )
                      : new Set(prev);
                    if (isExpanded) base.add(territory.id);
                    else base.delete(territory.id);
                    return base;
                  });
                }}
                sx={{
                  borderLeft: `4px solid ${borderColor}`,
                  '&:before': { display: 'none' },
                  ...(isPaused && { opacity: 0.7 }),
                }}
              >
                <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                  <Box display="flex" alignItems="center" gap={2} width="100%">
                    <Typography variant="h6" fontWeight={600} color={isPaused ? 'text.secondary' : 'text.primary'}>
                      {territory.name}
                    </Typography>
                    {isPaused && (
                      <Chip
                        icon={<PauseCircleOutlineIcon sx={{ fontSize: 16 }} />}
                        label="No Active ZIPs"
                        size="small"
                        color="default"
                        variant="outlined"
                        sx={{ fontWeight: 600 }}
                      />
                    )}
                    {!isPaused && displayVerticals.length > 0 && (
                      <Chip
                        label={`${displayVerticals.length} vertical${displayVerticals.length > 1 ? 's' : ''}`}
                        size="small"
                        variant="outlined"
                      />
                    )}
                    {territoryZipCount > 0 && (
                      <Chip
                        label={`${territoryStandardZipCount.toLocaleString()} ZIPs`}
                        size="small"
                        color="success"
                        variant="outlined"
                      />
                    )}
                    {territoryStandardZipCount !== territoryZipCount && (
                      <Tooltip title="PO Box and Unique ZIPs can't be targeted on Meta. They're excluded from the active count.">
                        <Chip
                          label={`+${(territoryZipCount - territoryStandardZipCount).toLocaleString()} non-targetable`}
                          size="small"
                          color="warning"
                          variant="outlined"
                        />
                      </Tooltip>
                    )}
                    {territoryPopulation > 0 && (
                      <Typography variant="body2" color="text.secondary">
                        {territoryPopulation.toLocaleString()} pop.
                      </Typography>
                    )}
                    {hasPending && (
                      <Chip label="pending" size="small" color="warning" />
                    )}
                    {(() => {
                      const meta = latestByTerritoryMap[territory.id];
                      if (!meta) return null;
                      const ago = formatDistanceToNow(meta.when, { addSuffix: true });
                      const by = meta.reviewedBy || meta.submittedBy;
                      return (
                        <Tooltip title={`Last approved ${meta.when.toLocaleString()}${by ? ` by ${by}` : ''}`}>
                          <Typography variant="caption" color="text.secondary" sx={{ ml: 0.5 }}>
                            Updated {ago}
                          </Typography>
                        </Tooltip>
                      );
                    })()}
                    <Box flexGrow={1} />
                    {(() => {
                      // Show territory-level Manage ZIPs when:
                      //  • single vertical (always)
                      //  • multi-vertical AND every vertical shares the
                      //    same ZIP set (the 95% case — edit all at once)
                      if (displayVerticals.length === 0) return null;
                      const sigs = new Set(
                        displayVerticals.map((v) => {
                          const zips = zipsByTerritoryVertical[`${territory.id}__${v.id}`] || [];
                          return zips.map((z) => z.zip).sort().join(',');
                        }),
                      );
                      const inSync = sigs.size === 1;
                      if (displayVerticals.length > 1 && !inSync) return null;
                      const target = displayVerticals.length === 1
                        ? displayVerticals[0]
                        : displayVerticals;
                      return (
                        <Button
                          size="small"
                          variant="outlined"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleOpenManageDialog(territory, target);
                          }}
                        >
                          Manage ZIPs
                          {displayVerticals.length > 1 ? ` (${displayVerticals.length} verticals)` : ''}
                        </Button>
                      );
                    })()}
                    <Tooltip title="Rename territory">
                      <IconButton
                        size="small"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleOpenRename(territory);
                        }}
                      >
                        <EditIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Pause entire territory (all verticals)">
                      <IconButton
                        size="small"
                        onClick={(e) => {
                          e.stopPropagation();
                          handlePauseTerritory(territory);
                        }}
                      >
                        <PauseCircleOutlineIcon />
                      </IconButton>
                    </Tooltip>
                  </Box>
                </AccordionSummary>
                <AccordionDetails>
                  {displayVerticals.length === 0 && (
                    <Box textAlign="center" py={3}>
                      <PlaceIcon sx={{ fontSize: 40, color: 'action.disabled', mb: 1 }} />
                      <Typography variant="body1" fontWeight={500} gutterBottom>
                        No ZIPs in this territory yet
                      </Typography>
                      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                        Select a vertical below, then add your ZIP codes.
                      </Typography>
                    </Box>
                  )}

                  {/* Add ZIPs for a vertical not yet in this territory */}
                  {(() => {
                    const unusedVerticals = availableVerticals.filter((v) => !usedVerticalIds.has(v.id));
                    if (unusedVerticals.length === 0) return null;
                    return (
                      <Box mb={2}>
                        <Autocomplete
                          options={unusedVerticals}
                          getOptionLabel={(v) => v.vertical}
                          onChange={(_, val) => {
                            if (val) handleOpenManageDialog(territory, val);
                          }}
                          value={null}
                          renderInput={(params) => (
                            <TextField
                              {...params}
                              label="Add ZIPs for a vertical..."
                              size="small"
                              sx={{ maxWidth: 300 }}
                            />
                          )}
                          blurOnSelect
                          clearOnBlur
                        />
                      </Box>
                    );
                  })()}

                  {(() => {
                    // Group verticals by identical ZIP sets so we render one row
                    // per unique coverage instead of N identical rows.
                    const groupsMap = new Map();
                    displayVerticals.forEach((v) => {
                      const key = `${territory.id}__${v.id}`;
                      const zips = zipsByTerritoryVertical[key] || [];
                      const sig = zips
                        .map((z) => z.zip)
                        .sort()
                        .join(',');
                      if (!groupsMap.has(sig)) {
                        groupsMap.set(sig, { verticals: [], zips });
                      }
                      groupsMap.get(sig).verticals.push(v);
                    });
                    const verticalGroups = [...groupsMap.values()];

                    return verticalGroups.map((group) => {
                      const zips = group.zips;
                      const verticals = group.verticals;
                      const isConsolidated = verticals.length > 1;
                      const groupKey = verticals.map((v) => v.id).sort().join('-');
                      const pendingSubs = verticals.flatMap(
                        (v) => pendingByTerritoryVertical[`${territory.id}__${v.id}`] || [],
                      );

                      return (
                      <Paper key={groupKey} variant="outlined" sx={{ p: 2, mb: 1.5 }}>
                        <Box display="flex" alignItems="center" justifyContent="space-between" mb={1}>
                          <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" rowGap={0.5}>
                            {isConsolidated ? (
                              <>
                                <Typography variant="subtitle1" fontWeight={600}>
                                  {verticals.length} verticals
                                </Typography>
                                <Tooltip title="These verticals share an identical ZIP set. Click a vertical to edit it on its own.">
                                  <Stack direction="row" spacing={0.5}>
                                    {verticals.map((v) => (
                                      <Chip
                                        key={v.id}
                                        label={v.vertical}
                                        size="small"
                                        variant="outlined"
                                        onClick={() => handleOpenManageDialog(territory, v)}
                                        sx={{ cursor: 'pointer' }}
                                      />
                                    ))}
                                  </Stack>
                                </Tooltip>
                              </>
                            ) : (
                              <Typography variant="subtitle1" fontWeight={600}>
                                {verticals[0].vertical}
                              </Typography>
                            )}
                            {(() => {
                              const standardCount = zips.filter(
                                (z) => (z.zip_type || '').toUpperCase() === 'STANDARD',
                              ).length;
                              const excluded = zips.length - standardCount;
                              const standardPop = zips.reduce(
                                (s, z) => s + ((z.zip_type || '').toUpperCase() === 'STANDARD' ? (z.population || 0) : 0),
                                0,
                              );
                              return (
                                <>
                                  <Chip
                                    label={`${standardCount.toLocaleString()} active`}
                                    size="small"
                                    color="success"
                                    variant="outlined"
                                  />
                                  {standardPop > 0 && (
                                    <Chip
                                      label={`${standardPop.toLocaleString()} pop.`}
                                      size="small"
                                      variant="outlined"
                                      sx={{ fontWeight: 500 }}
                                    />
                                  )}
                                  {excluded > 0 && (
                                    <Tooltip title="PO Box and Unique ZIPs can't be targeted on Meta. They're excluded from the active count.">
                                      <Chip
                                        label={`+${excluded.toLocaleString()} non-targetable`}
                                        size="small"
                                        color="warning"
                                        variant="outlined"
                                      />
                                    </Tooltip>
                                  )}
                                </>
                              );
                            })()}
                            {pendingSubs.length > 0 && (
                              <Chip
                                label={`${pendingSubs.length} pending`}
                                size="small"
                                color="warning"
                              />
                            )}
                          </Stack>
                          <Stack direction="row" spacing={1}>
                            <Button
                              size="small"
                              variant="outlined"
                              onClick={() => {
                                // Consolidated rows share one ZIP set across
                                // multiple verticals — edit them all in one
                                // submission. User can opt into per-vertical
                                // editing from inside the dialog.
                                handleOpenManageDialog(
                                  territory,
                                  isConsolidated ? verticals : verticals[0],
                                );
                              }}
                            >
                              Manage ZIPs
                            </Button>
                            <Tooltip title={isConsolidated ? 'Pause a vertical' : 'Pause this vertical'}>
                              <IconButton
                                size="small"
                                onClick={(e) => {
                                  if (isConsolidated) {
                                    setGroupManageMenu({
                                      anchorEl: e.currentTarget,
                                      territory,
                                      verticals,
                                      action: 'pause',
                                    });
                                  } else {
                                    handlePauseTerritory(territory, verticals[0]);
                                  }
                                }}
                              >
                                <PauseCircleOutlineIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          </Stack>
                        </Box>

                        {/* County roll-up — counties hidden by default; click to reveal */}
                        {zips.length === 0 ? (
                          <Typography variant="body2" color="text.secondary">
                            No active ZIPs
                          </Typography>
                        ) : (
                          (() => {
                            const byCounty = new Map();
                            zips.forEach((czv) => {
                              const county = czv.county || '—';
                              const state = czv.state || '';
                              const gkey = `${county}|${state}`;
                              if (!byCounty.has(gkey)) {
                                byCounty.set(gkey, { county, state, zips: [] });
                              }
                              byCounty.get(gkey).zips.push(czv);
                            });
                            const groups = [...byCounty.values()].sort((a, b) => {
                              if (b.zips.length !== a.zips.length) return b.zips.length - a.zips.length;
                              return a.county.localeCompare(b.county);
                            });
                            const vKey = `${territory.id}__group${groupKey}`;
                            const countiesOpen = expandedVerticalCountiesKeys.has(vKey);
                            const stateSummary = (() => {
                              const states = new Set(
                                groups.map((g) => g.state).filter(Boolean),
                              );
                              if (states.size === 0) return '';
                              if (states.size === 1) return ` · ${[...states][0]}`;
                              return ` · ${states.size} states`;
                            })();
                            return (
                              <>
                                <Button
                                  size="small"
                                  variant="text"
                                  startIcon={
                                    <ExpandMoreIcon
                                      sx={{
                                        transform: countiesOpen ? 'rotate(0deg)' : 'rotate(-90deg)',
                                        transition: 'transform 150ms',
                                      }}
                                    />
                                  }
                                  onClick={() => {
                                    setExpandedVerticalCountiesKeys((prev) => {
                                      const next = new Set(prev);
                                      if (next.has(vKey)) next.delete(vKey);
                                      else next.add(vKey);
                                      return next;
                                    });
                                  }}
                                  sx={{ textTransform: 'none', px: 0.5, mb: 0.5, color: 'text.secondary' }}
                                >
                                  {countiesOpen ? 'Hide counties' : `Show ${groups.length} ${groups.length === 1 ? 'county' : 'counties'}${stateSummary}`}
                                </Button>
                                {countiesOpen && (
                                  <Stack spacing={0.5}>
                                {groups.map((g) => {
                                  const ek = `${territory.id}__group${groupKey}__${g.county}|${g.state}`;
                                  const isOpen = expandedCountyKeys.has(ek);
                                  const pop = g.zips.reduce((s, z) => s + (z.population || 0), 0);
                                  const standardCount = g.zips.filter(
                                    (z) => (z.zip_type || '').toUpperCase() === 'STANDARD',
                                  ).length;
                                  return (
                                    <Box key={ek}>
                                      <Box
                                        onClick={() => {
                                          setExpandedCountyKeys((prev) => {
                                            const next = new Set(prev);
                                            if (next.has(ek)) next.delete(ek);
                                            else next.add(ek);
                                            return next;
                                          });
                                        }}
                                        sx={{
                                          display: 'flex',
                                          alignItems: 'center',
                                          gap: 1,
                                          px: 1,
                                          py: 0.5,
                                          borderRadius: 1,
                                          cursor: 'pointer',
                                          '&:hover': { bgcolor: 'action.hover' },
                                        }}
                                      >
                                        <ExpandMoreIcon
                                          sx={{
                                            fontSize: 18,
                                            transform: isOpen ? 'rotate(0deg)' : 'rotate(-90deg)',
                                            transition: 'transform 150ms',
                                            color: 'action.active',
                                          }}
                                        />
                                        <Typography variant="body2" fontWeight={600}>
                                          {g.county}{g.state ? `, ${g.state}` : ''}
                                        </Typography>
                                        <Chip
                                          label={`${g.zips.length} ZIP${g.zips.length === 1 ? '' : 's'}`}
                                          size="small"
                                          variant="outlined"
                                          sx={{ height: 20, fontSize: '0.7rem' }}
                                        />
                                        {standardCount !== g.zips.length && (
                                          <Tooltip title="PO Box / Unique ZIPs in this county can't be targeted on Meta.">
                                            <Chip
                                              label={`+${(g.zips.length - standardCount).toLocaleString()} non-targetable`}
                                              size="small"
                                              color="warning"
                                              variant="outlined"
                                              sx={{ height: 20, fontSize: '0.7rem' }}
                                            />
                                          </Tooltip>
                                        )}
                                        {pop > 0 && (
                                          <Typography variant="caption" color="text.secondary">
                                            {pop.toLocaleString()} pop.
                                          </Typography>
                                        )}
                                      </Box>
                                      {isOpen && (
                                        <Box display="flex" flexWrap="wrap" gap={0.5} sx={{ pl: 3.5, pb: 0.75 }}>
                                          {g.zips.map((czv) => (
                                            <Tooltip
                                              key={czv.id}
                                              title={czv.city && czv.state ? `${czv.city}, ${czv.state}` : ''}
                                              arrow
                                            >
                                              <Chip
                                                label={czv.zip}
                                                size="small"
                                                variant="outlined"
                                                color={(czv.zip_type || '').toUpperCase() === 'STANDARD' ? 'default' : 'warning'}
                                              />
                                            </Tooltip>
                                          ))}
                                        </Box>
                                      )}
                                    </Box>
                                  );
                                })}
                                  </Stack>
                                )}
                              </>
                            );
                          })()
                        )}

                        {/* Pending submissions for this territory+vertical */}
                        {pendingSubs.map((sub) => {
                          const adds = (sub.items || []).filter((i) => i.action === 'add');
                          const removes = (sub.items || []).filter((i) => i.action === 'remove');
                          return (
                            <Alert
                              key={sub.id}
                              severity="warning"
                              sx={{ mt: 1 }}
                              action={
                                <Button
                                  size="small"
                                  color="inherit"
                                  onClick={() => handleCancelSubmissionOpen(sub.id)}
                                >
                                  Cancel
                                </Button>
                              }
                            >
                              <Typography variant="body2" fontWeight={600} sx={{ mb: 0.5 }}>
                                {isConsolidated && sub.vertical_name ? `${sub.vertical_name} — ` : ''}
                                Pending — submitted {new Date(sub.submitted_at).toLocaleDateString()}
                              </Typography>
                              {adds.length > 0 && (
                                <Box display="flex" flexWrap="wrap" gap={0.5} alignItems="center" mb={0.5}>
                                  <Typography variant="caption" color="text.secondary" sx={{ mr: 0.5 }}>
                                    Adding:
                                  </Typography>
                                  {adds.slice(0, 30).map((item) => (
                                    <Chip
                                      key={item.id}
                                      label={item.zip || item.zip_raw}
                                      size="small"
                                      color="success"
                                      variant="outlined"
                                      sx={{ height: 20, fontSize: '0.7rem' }}
                                    />
                                  ))}
                                  {adds.length > 30 && (
                                    <Chip label={`+${adds.length - 30} more`} size="small" sx={{ height: 20, fontSize: '0.7rem' }} />
                                  )}
                                </Box>
                              )}
                              {removes.length > 0 && (
                                <Box display="flex" flexWrap="wrap" gap={0.5} alignItems="center">
                                  <Typography variant="caption" color="text.secondary" sx={{ mr: 0.5 }}>
                                    Removing:
                                  </Typography>
                                  {removes.slice(0, 30).map((item) => (
                                    <Chip
                                      key={item.id}
                                      label={item.zip || item.zip_raw}
                                      size="small"
                                      color="error"
                                      variant="outlined"
                                      sx={{ height: 20, fontSize: '0.7rem' }}
                                    />
                                  ))}
                                  {removes.length > 30 && (
                                    <Chip label={`+${removes.length - 30} more`} size="small" sx={{ height: 20, fontSize: '0.7rem' }} />
                                  )}
                                </Box>
                              )}
                            </Alert>
                          );
                        })}
                      </Paper>
                      );
                    });
                  })()}
                </AccordionDetails>
              </Accordion>
            );
          })}

          {/* Shared menu for consolidated verticals — Manage or Pause */}
          <Menu
            open={Boolean(groupManageMenu)}
            anchorEl={groupManageMenu?.anchorEl}
            onClose={() => setGroupManageMenu(null)}
          >
            {(groupManageMenu?.verticals || []).map((v) => (
              <MenuItem
                key={v.id}
                onClick={() => {
                  const { territory, action } = groupManageMenu;
                  setGroupManageMenu(null);
                  if (action === 'manage') handleOpenManageDialog(territory, v);
                  else if (action === 'pause') handlePauseTerritory(territory, v);
                }}
              >
                {groupManageMenu?.action === 'pause' ? `Pause ${v.vertical}` : v.vertical}
              </MenuItem>
            ))}
          </Menu>
        </>
      )}

      {/* ═══════════ TAB 1: Map ═══════════ */}
      {selectedClientId && !loading && activeTab === 1 && (
        <Box>
          <ClientZipMap
            clientId={selectedClientId}
            skipLeads
          />
        </Box>
      )}

      {/* ═══════════ TAB 2: History ═══════════ */}
      {selectedClientId && !loading && activeTab === 2 && (
        <Box>
          {/* History filters */}
          <Box display="flex" gap={2} mb={2} alignItems="center">
            <FormControl size="small" sx={{ minWidth: 150 }}>
              <InputLabel>Status</InputLabel>
              <Select
                value={historyFilter}
                label="Status"
                onChange={(e) => setHistoryFilter(e.target.value)}
              >
                <MenuItem value="all">All</MenuItem>
                <MenuItem value="pending">Pending</MenuItem>
                <MenuItem value="approved">Approved</MenuItem>
                <MenuItem value="rejected">Rejected</MenuItem>
                <MenuItem value="cancelled">Cancelled</MenuItem>
              </Select>
            </FormControl>
          </Box>

          {historyLoading && historySubmissions.length === 0 && (
            <Box display="flex" justifyContent="center" my={4}>
              <CircularProgress />
            </Box>
          )}

          {!historyLoading && historySubmissions.length === 0 && (
            <Alert severity="info">No submission history found.</Alert>
          )}

          {historySubmissions.length > 0 && (
            <Stack spacing={1.5}>
              {historySubmissions.map((sub) => {
                const cfg = STATUS_CONFIG[sub.status] || STATUS_CONFIG.pending;
                const adds = (sub.items || []).filter((i) => i.action === 'add');
                const removes = (sub.items || []).filter((i) => i.action === 'remove');
                return (
                  <Paper key={sub.id} variant="outlined" sx={{ p: 2 }}>
                    <Box display="flex" alignItems="center" justifyContent="space-between" mb={1}>
                      <Stack direction="row" spacing={1} alignItems="center">
                        <Typography variant="subtitle2">
                          {sub.territory_name || 'Unknown Territory'} / {sub.vertical_name || 'Unknown'}
                        </Typography>
                        <Chip label={cfg.label} size="small" color={cfg.color} />
                      </Stack>
                      <Typography variant="caption" color="text.secondary">
                        {new Date(sub.submitted_at).toLocaleDateString()}{' '}
                        {new Date(sub.submitted_at).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                      </Typography>
                    </Box>

                    <Stack direction="row" spacing={2} mb={1}>
                      {sub.add_count > 0 && (
                        <Typography variant="body2" color="success.main">
                          +{sub.add_count} add{sub.add_count !== 1 ? 's' : ''}
                        </Typography>
                      )}
                      {sub.remove_count > 0 && (
                        <Typography variant="body2" color="error.main">
                          -{sub.remove_count} removal{sub.remove_count !== 1 ? 's' : ''}
                        </Typography>
                      )}
                    </Stack>

                    {adds.length > 0 && (
                      <Box display="flex" flexWrap="wrap" gap={0.5} mb={0.5}>
                        {adds.slice(0, 20).map((item) => (
                          <Chip
                            key={item.id}
                            label={item.zip || item.zip_raw}
                            size="small"
                            color="success"
                            variant="outlined"
                            sx={{ height: 20, fontSize: '0.7rem' }}
                          />
                        ))}
                        {adds.length > 20 && (
                          <Chip label={`+${adds.length - 20} more`} size="small" sx={{ height: 20, fontSize: '0.7rem' }} />
                        )}
                      </Box>
                    )}
                    {removes.length > 0 && (
                      <Box display="flex" flexWrap="wrap" gap={0.5} mb={0.5}>
                        {removes.slice(0, 20).map((item) => (
                          <Chip
                            key={item.id}
                            label={item.zip || item.zip_raw}
                            size="small"
                            color="error"
                            variant="outlined"
                            sx={{ height: 20, fontSize: '0.7rem' }}
                          />
                        ))}
                        {removes.length > 20 && (
                          <Chip label={`+${removes.length - 20} more`} size="small" sx={{ height: 20, fontSize: '0.7rem' }} />
                        )}
                      </Box>
                    )}

                    {sub.reviewer_notes && (
                      <Alert severity={sub.status === 'rejected' ? 'error' : 'info'} sx={{ mt: 1 }} variant="outlined">
                        <Typography variant="body2">
                          <strong>Reviewer note:</strong> {sub.reviewer_notes}
                        </Typography>
                      </Alert>
                    )}

                    {sub.reviewed_at && (
                      <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block' }}>
                        Reviewed {new Date(sub.reviewed_at).toLocaleDateString()} by {sub.reviewed_by_email || 'staff'}
                      </Typography>
                    )}

                    <Box mt={1} display="flex" gap={1}>
                      {(() => {
                        const hasGeo = (sub.items || []).some(
                          (i) => i.latitude != null && i.longitude != null,
                        );
                        if (!hasGeo) return null;
                        const open = historyMapOpenIds.has(sub.id);
                        return (
                          <Button
                            size="small"
                            variant="outlined"
                            startIcon={<MapIcon />}
                            onClick={() => {
                              setHistoryMapOpenIds((prev) => {
                                const next = new Set(prev);
                                if (next.has(sub.id)) next.delete(sub.id);
                                else next.add(sub.id);
                                return next;
                              });
                            }}
                          >
                            {open ? 'Hide map' : 'Show map'}
                          </Button>
                        );
                      })()}
                      {sub.status === 'pending' && (
                        <Button
                          size="small"
                          color="warning"
                          variant="outlined"
                          onClick={() => handleCancelSubmissionOpen(sub.id)}
                        >
                          Cancel Submission
                        </Button>
                      )}
                    </Box>

                    {historyMapOpenIds.has(sub.id) && (
                      <Box mt={1.5}>
                        <SubmissionDiffMap items={sub.items || []} />
                      </Box>
                    )}
                  </Paper>
                );
              })}

              {/* Load more */}
              {historyHasMore && (
                <Box display="flex" justifyContent="center" mt={1}>
                  <Button
                    variant="outlined"
                    onClick={() => loadHistory(historyPage + 1, historyFilter, true)}
                    disabled={historyLoading}
                    startIcon={historyLoading ? <CircularProgress size={16} /> : null}
                  >
                    Load More
                  </Button>
                </Box>
              )}
            </Stack>
          )}
        </Box>
      )}

      {/* ─── Create Territory Dialog ──────────────────────────────────── */}
      <Dialog
        open={newTerritoryDialogOpen}
        onClose={() => setNewTerritoryDialogOpen(false)}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>Create Territory</DialogTitle>
        <DialogContent>
          {newTerritoryError && (
            <Alert severity="error" sx={{ mb: 2 }}>{newTerritoryError}</Alert>
          )}
          <TextField
            autoFocus
            fullWidth
            label="Territory Name"
            value={newTerritoryName}
            onChange={(e) => setNewTerritoryName(e.target.value)}
            sx={{ mt: 1 }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setNewTerritoryDialogOpen(false)}>Cancel</Button>
          <Button
            variant="contained"
            onClick={handleCreateTerritory}
            disabled={newTerritorySaving}
          >
            {newTerritorySaving ? <CircularProgress size={18} /> : 'Create'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* ─── Rename Territory Dialog ──────────────────────────────────── */}
      <Dialog
        open={renameTerritoryDialogOpen}
        onClose={() => setRenameTerritoryDialogOpen(false)}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>Rename Territory</DialogTitle>
        <DialogContent>
          {renameTerritoryError && (
            <Alert severity="error" sx={{ mb: 2 }}>{renameTerritoryError}</Alert>
          )}
          <TextField
            autoFocus
            fullWidth
            label="New Name"
            value={renameTerritoryName}
            onChange={(e) => setRenameTerritoryName(e.target.value)}
            sx={{ mt: 1 }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setRenameTerritoryDialogOpen(false)}>Cancel</Button>
          <Button
            variant="contained"
            onClick={handleRenameTerritory}
            disabled={renameTerritorySaving}
          >
            {renameTerritorySaving ? <CircularProgress size={18} /> : 'Rename'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* ─── Manage ZIPs Dialog ───────────────────────────────────────── */}
      <Dialog
        open={manageDialogOpen}
        onClose={() => setManageDialogOpen(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle component="div" sx={{ pb: 1 }}>
          <Typography variant="h6" component="div">
            Manage ZIPs — {manageTerritory?.name}
            {' / '}
            {isMultiVerticalManage
              ? `${manageVerticals.length} verticals`
              : manageAnchorVertical?.vertical}
          </Typography>
          {isMultiVerticalManage && (
            <Stack direction="row" spacing={0.5} sx={{ mt: 1, flexWrap: 'wrap', rowGap: 0.5, alignItems: 'center' }}>
              <Tooltip title="Changes will apply to all of these verticals — one submission per vertical, grouped as one batch.">
                <Chip
                  label="Applies to all"
                  size="small"
                  color="primary"
                  variant="filled"
                  sx={{ fontWeight: 600 }}
                />
              </Tooltip>
              {manageVerticals.map((v) => (
                <Chip
                  key={v.id}
                  label={v.vertical}
                  size="small"
                  variant="outlined"
                />
              ))}
              <Box flexGrow={1} />
              <Button
                size="small"
                variant="text"
                onClick={(e) => setCustomizeAnchorEl(e.currentTarget)}
                sx={{ textTransform: 'none' }}
              >
                Customize per vertical
              </Button>
              <Menu
                anchorEl={customizeAnchorEl}
                open={Boolean(customizeAnchorEl)}
                onClose={() => setCustomizeAnchorEl(null)}
              >
                {manageVerticals.map((v) => (
                  <MenuItem
                    key={v.id}
                    onClick={() => {
                      setCustomizeAnchorEl(null);
                      handleOpenManageDialog(manageTerritory, v);
                    }}
                  >
                    Edit {v.vertical} only
                  </MenuItem>
                ))}
              </Menu>
            </Stack>
          )}
        </DialogTitle>
        <DialogContent dividers>
          {manageError && (
            <Alert severity="error" sx={{ mb: 2 }}>{manageError}</Alert>
          )}

          <Tabs
            value={manageMode}
            onChange={(_, v) => {
              setManageMode(v);
              setManageZipText('');
              setManageRemoveIds([]);
              setManageValidation(null);
              setManageError('');
            }}
            sx={{ mb: 2 }}
          >
            <Tab value="add" label="Add ZIPs" />
            <Tab value="replace" label="Replace All" />
            <Tab value="remove" label="Remove ZIPs" />
          </Tabs>

          <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
            {manageMode === 'add' && 'Paste ZIP codes to add (comma, tab, or newline separated) or upload a CSV file.'}
            {manageMode === 'replace' && 'Paste the complete set of ZIPs you want. Current ZIPs not in this list will be removed, new ones will be added.'}
            {manageMode === 'remove' && 'Paste ZIP codes to remove from this territory/vertical.'}
          </Typography>

          <Stack direction="row" spacing={1} sx={{ mb: 1 }}>
            <Button
              size="small"
              variant="outlined"
              startIcon={<UploadFileIcon />}
              onClick={() => fileInputRef.current?.click()}
            >
              Upload CSV
            </Button>
            <input
              ref={fileInputRef}
              type="file"
              accept=".csv,.txt,.tsv"
              style={{ display: 'none' }}
              onChange={handleFileUpload}
            />
            {manageMode !== 'remove' && (
              <Button
                size="small"
                variant="outlined"
                onClick={(e) => setCopyFromAnchorEl(e.currentTarget)}
                disabled={copySourceOptions.length === 0}
              >
                Copy from another vertical
              </Button>
            )}
            <Menu
              open={Boolean(copyFromAnchorEl)}
              anchorEl={copyFromAnchorEl}
              onClose={() => setCopyFromAnchorEl(null)}
            >
              {copySourceOptions.length === 0 ? (
                <MenuItem disabled>No other verticals with ZIPs</MenuItem>
              ) : (
                copySourceOptions.map((opt) => (
                  <MenuItem
                    key={opt.key}
                    onClick={() => {
                      const zipCodes = opt.zips.map((z) => z.zip).join('\n');
                      setManageZipText(zipCodes);
                      setCopyFromAnchorEl(null);
                    }}
                  >
                    <Stack direction="column" spacing={0} alignItems="flex-start">
                      <Typography variant="body2" fontWeight={600}>
                        {opt.territoryName} / {opt.verticalName}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {opt.zips.length.toLocaleString()} standard ZIP{opt.zips.length === 1 ? '' : 's'}
                      </Typography>
                    </Stack>
                  </MenuItem>
                ))
              )}
            </Menu>
          </Stack>

          <TextField
            multiline
            rows={4}
            fullWidth
            placeholder="10001, 10002, 10003..."
            value={manageZipText}
            onChange={(e) => setManageZipText(e.target.value)}
            sx={{ mb: 1 }}
          />

          {/* Live validation feedback */}
          {manageValidation && (
            <Paper variant="outlined" sx={{ p: 1.5, mb: 2, bgcolor: 'background.default' }}>
              <Stack spacing={0.75}>
                {manageMode === 'remove' ? (
                  <>
                    <Box display="flex" alignItems="center" gap={1} flexWrap="wrap">
                      <Chip
                        label={`${manageValidation.matched?.length || 0} to remove`}
                        size="small"
                        color="error"
                        variant="outlined"
                      />
                      {(manageValidation.notFound?.length || 0) > 0 && (
                        <Chip
                          label={`${manageValidation.notFound.length} not found (skipped)`}
                          size="small"
                          color="default"
                          variant="outlined"
                        />
                      )}
                    </Box>
                    {manageValidation.matched?.length > 0 && (
                      <Typography variant="caption" color="error.main">
                        Ready to submit removal of {manageValidation.matched.length} ZIP{manageValidation.matched.length > 1 ? 's' : ''}.
                      </Typography>
                    )}
                  </>
                ) : manageMode === 'replace' ? (
                  <>
                    <Box display="flex" alignItems="center" gap={1} flexWrap="wrap">
                      {manageValidation.newZips.length > 0 && (
                        <Chip
                          label={`${manageValidation.newZips.length} to add`}
                          size="small"
                          color="success"
                          variant="outlined"
                        />
                      )}
                      {(manageValidation.toRemove?.length || 0) > 0 && (
                        <Chip
                          label={`${manageValidation.toRemove.length} to remove`}
                          size="small"
                          color="error"
                          variant="outlined"
                        />
                      )}
                      {manageValidation.dupes.length > 0 && (
                        <Chip
                          label={`${manageValidation.dupes.length} unchanged`}
                          size="small"
                          color="default"
                          variant="outlined"
                        />
                      )}
                      {manageValidating && (
                        <Stack direction="row" spacing={0.5} alignItems="center">
                          <CircularProgress size={14} />
                          <Typography variant="caption" color="text.secondary">Checking...</Typography>
                        </Stack>
                      )}
                    </Box>
                    {manageValidation.apiChecked && manageValidation.unknown.length > 0 && (
                      <Alert
                        severity="error"
                        variant="outlined"
                        sx={{ py: 0.25 }}
                        action={
                          <Button size="small" color="error" onClick={handleRemoveInvalidZips}>
                            Remove
                          </Button>
                        }
                      >
                        <Typography variant="caption">
                          Unknown ZIPs: {manageValidation.unknown.slice(0, 10).join(', ')}
                          {manageValidation.unknown.length > 10 ? ` +${manageValidation.unknown.length - 10} more` : ''}
                        </Typography>
                      </Alert>
                    )}
                    {manageValidation.apiChecked && manageValidation.conflicts.length > 0 && (
                      <Alert severity="warning" variant="outlined" sx={{ py: 0.25 }}>
                        <Typography variant="caption">
                          Conflicts: {manageValidation.conflicts.slice(0, 5).map((c) => `${c.zip} (${c.current_territory})`).join(', ')}
                          {manageValidation.conflicts.length > 5 ? ` +${manageValidation.conflicts.length - 5} more` : ''}
                        </Typography>
                      </Alert>
                    )}
                    {manageValidation.apiChecked
                      && manageValidation.unknown.length === 0
                      && manageValidation.conflicts.length === 0 && (
                      <Typography variant="caption" color="success.main">
                        Validated — {manageValidation.newZips.length} add{manageValidation.newZips.length !== 1 ? 's' : ''}, {manageValidation.toRemove?.length || 0} removal{(manageValidation.toRemove?.length || 0) !== 1 ? 's' : ''}.
                      </Typography>
                    )}
                  </>
                ) : (
                  <>
                    <Box display="flex" alignItems="center" gap={1} flexWrap="wrap">
                      <Chip
                        label={`${manageValidation.newZips.length} new`}
                        size="small"
                        color="success"
                        variant="outlined"
                      />
                      {manageValidation.dupes.length > 0 && (
                        <Chip
                          label={`${manageValidation.dupes.length} already active (skipped)`}
                          size="small"
                          color="default"
                          variant="outlined"
                        />
                      )}
                      {manageValidation.inputDupes > 0 && (
                        <Chip
                          label={`${manageValidation.inputDupes} duplicate input${manageValidation.inputDupes > 1 ? 's' : ''}`}
                          size="small"
                          color="default"
                          variant="outlined"
                        />
                      )}
                      {manageValidating && (
                        <Stack direction="row" spacing={0.5} alignItems="center">
                          <CircularProgress size={14} />
                          <Typography variant="caption" color="text.secondary">Checking...</Typography>
                        </Stack>
                      )}
                    </Box>

                    {manageValidation.apiChecked && manageValidation.unknown.length > 0 && (
                      <Alert
                        severity="error"
                        variant="outlined"
                        sx={{ py: 0.25 }}
                        action={
                          <Button size="small" color="error" onClick={handleRemoveInvalidZips}>
                            Remove
                          </Button>
                        }
                      >
                        <Typography variant="caption">
                          Unknown ZIPs: {manageValidation.unknown.slice(0, 10).join(', ')}
                          {manageValidation.unknown.length > 10 ? ` +${manageValidation.unknown.length - 10} more` : ''}
                        </Typography>
                      </Alert>
                    )}

                    {manageValidation.apiChecked && manageValidation.conflicts.length > 0 && (
                      <Alert severity="warning" variant="outlined" sx={{ py: 0.25 }}>
                        <Typography variant="caption">
                          Conflicts: {manageValidation.conflicts.slice(0, 5).map((c) => `${c.zip} (${c.current_territory})`).join(', ')}
                          {manageValidation.conflicts.length > 5 ? ` +${manageValidation.conflicts.length - 5} more` : ''}
                        </Typography>
                      </Alert>
                    )}

                    {manageValidation.apiChecked
                      && manageValidation.unknown.length === 0
                      && manageValidation.conflicts.length === 0
                      && manageValidation.newZips.length > 0 && (
                      <>
                        <Typography variant="caption" color="success.main">
                          All ZIPs validated — ready to submit.
                        </Typography>
                        {manageValidation.zipDetails && Object.keys(manageValidation.zipDetails).length > 0 && (
                          <Box display="flex" flexWrap="wrap" gap={0.5} mt={0.5}>
                            {manageValidation.newZips.slice(0, 30).map((z) => {
                              const d = manageValidation.zipDetails[z];
                              return (
                                <Tooltip key={z} title={d?.city && d?.state ? `${d.city}, ${d.state}` : ''} arrow>
                                  <Chip label={z} size="small" color="success" variant="outlined" />
                                </Tooltip>
                              );
                            })}
                            {manageValidation.newZips.length > 30 && (
                              <Chip label={`+${manageValidation.newZips.length - 30} more`} size="small" />
                            )}
                          </Box>
                        )}
                      </>
                    )}
                  </>
                )}
              </Stack>
            </Paper>
          )}

          <Divider sx={{ mb: 2 }} />

          <Typography variant="subtitle2" gutterBottom>
            Current ZIPs ({currentManagedZips.length})
            {manageMode === 'add' && manageRemoveIds.length > 0 && (
              <Chip
                label={`${manageRemoveIds.length} marked for removal`}
                size="small"
                color="error"
                sx={{ ml: 1 }}
              />
            )}
            {manageMode === 'replace' && manageValidation?.toRemove?.length > 0 && (
              <Chip
                label={`${manageValidation.toRemove.length} will be removed`}
                size="small"
                color="error"
                sx={{ ml: 1 }}
              />
            )}
          </Typography>
          {manageMode === 'add' && (
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
              Click a ZIP to toggle it for removal.
            </Typography>
          )}
          <Box display="flex" flexWrap="wrap" gap={0.5} sx={{ maxHeight: 300, overflow: 'auto' }}>
            {currentManagedZips.map((czv) => {
              const isMarkedForRemoval = manageMode === 'add'
                ? manageRemoveIds.includes(czv.id)
                : manageMode === 'replace'
                  ? manageValidation?.toRemove?.some((r) => r.id === czv.id)
                  : manageValidation?.matched?.includes(czv.zip);
              return (
                <Tooltip key={czv.id} title={czv.city && czv.state ? `${czv.city}, ${czv.state}` : ''} arrow>
                  <Chip
                    label={czv.zip}
                    size="small"
                    color={isMarkedForRemoval ? 'error' : 'default'}
                    variant={isMarkedForRemoval ? 'filled' : 'outlined'}
                    onClick={manageMode === 'add' ? () => handleToggleRemove(czv.id) : undefined}
                    sx={manageMode === 'add' ? { cursor: 'pointer' } : undefined}
                  />
                </Tooltip>
              );
            })}
          </Box>
          <TextField
            label="Note (optional)"
            placeholder="e.g. expanding to south FL, seasonal pause..."
            value={submitterNotes}
            onChange={(e) => setSubmitterNotes(e.target.value)}
            size="small"
            fullWidth
            multiline
            minRows={2}
            maxRows={4}
            sx={{ mt: 2 }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setManageDialogOpen(false)}>Cancel</Button>
          <Button
            variant="contained"
            onClick={handleSubmitZipChanges}
            disabled={manageSubmitting}
            startIcon={manageSubmitting ? <CircularProgress size={16} /> : null}
          >
            Submit for Review
          </Button>
        </DialogActions>
      </Dialog>

      {/* ─── Bulk Upload Dialog ───────────────────────────────────────── */}
      <Dialog
        open={bulkUploadOpen}
        onClose={() => setBulkUploadOpen(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>Bulk ZIP Upload</DialogTitle>
        <DialogContent dividers>
          {bulkError && (
            <Alert severity="error" sx={{ mb: 2, whiteSpace: 'pre-line' }}>{bulkError}</Alert>
          )}

          <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
            Upload a CSV with columns: <strong>Territory, Vertical, ZIP, Action</strong> (action is optional, defaults to &quot;add&quot;).
            Territory and vertical names must match existing ones exactly.
          </Typography>

          <Stack direction="row" spacing={1} sx={{ mb: 1 }}>
            <Button
              size="small"
              variant="outlined"
              startIcon={<UploadFileIcon />}
              onClick={() => bulkFileInputRef.current?.click()}
            >
              Upload CSV
            </Button>
            <input
              ref={bulkFileInputRef}
              type="file"
              accept=".csv,.txt,.tsv"
              style={{ display: 'none' }}
              onChange={handleBulkFileUpload}
            />
            <Button
              size="small"
              variant="outlined"
              startIcon={<DownloadIcon />}
              onClick={handleDownloadTemplate}
            >
              Download Template
            </Button>
          </Stack>

          <TextField
            multiline
            rows={6}
            fullWidth
            placeholder={"Territory,Vertical,ZIP,Action\nPhoenix,Flooring,85001,add\nTucson,Flooring,85710,remove"}
            value={bulkCsvText}
            onChange={(e) => setBulkCsvText(e.target.value)}
            sx={{ mb: 2, fontFamily: 'monospace', fontSize: 13 }}
            InputProps={{ sx: { fontFamily: 'monospace', fontSize: 13 } }}
          />

          {/* Parse errors */}
          {bulkParsed?.errors?.length > 0 && (
            <Paper variant="outlined" sx={{ p: 1.5, mb: 2, maxHeight: 200, overflow: 'auto' }}>
              <Typography variant="subtitle2" color="error" gutterBottom>
                Unmatched Names ({bulkParsed.errors.length})
              </Typography>
              {bulkParsed.errors.slice(0, 20).map((err, i) => (
                <Typography key={i} variant="caption" display="block" color="text.secondary">
                  Row {err.row}: {err.field} &quot;{err.value}&quot; not found
                  {err.suggestions?.length > 0 && ` — did you mean: ${err.suggestions.join(', ')}?`}
                </Typography>
              ))}
              {bulkParsed.errors.length > 20 && (
                <Typography variant="caption" color="text.secondary">
                  +{bulkParsed.errors.length - 20} more errors
                </Typography>
              )}
            </Paper>
          )}

          {/* Summary table */}
          {bulkParsed?.groups?.length > 0 && bulkParsed.errors.length === 0 && (
            <Paper variant="outlined" sx={{ p: 1.5, mb: 2 }}>
              <Typography variant="subtitle2" gutterBottom>
                Summary — {bulkParsed.groups.length} territory/vertical group{bulkParsed.groups.length > 1 ? 's' : ''}
              </Typography>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Territory</TableCell>
                    <TableCell>Vertical</TableCell>
                    <TableCell align="right">Adds</TableCell>
                    <TableCell align="right">Removes</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {bulkParsed.groups.map((g, i) => (
                    <TableRow key={i}>
                      <TableCell>{g.territory.name}</TableCell>
                      <TableCell>{g.vertical?.vertical || '—'}</TableCell>
                      <TableCell align="right">
                        {g.adds.length > 0 && (
                          <Chip label={g.adds.length} size="small" color="success" variant="outlined" />
                        )}
                      </TableCell>
                      <TableCell align="right">
                        {g.removes.length > 0 && (
                          <Chip label={g.removes.length} size="small" color="error" variant="outlined" />
                        )}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </Paper>
          )}

          <TextField
            label="Note (optional)"
            placeholder="e.g. Q2 territory expansion, seasonal update..."
            value={bulkNotes}
            onChange={(e) => setBulkNotes(e.target.value)}
            size="small"
            fullWidth
            multiline
            minRows={2}
            maxRows={4}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setBulkUploadOpen(false)}>Cancel</Button>
          <Button
            variant="contained"
            onClick={handleBulkSubmit}
            disabled={bulkSubmitting || !bulkParsed?.groups?.length || bulkParsed?.errors?.length > 0}
            startIcon={bulkSubmitting ? <CircularProgress size={16} /> : null}
          >
            Submit {bulkParsed?.groups?.length > 0 ? `(${bulkParsed.groups.length} group${bulkParsed.groups.length > 1 ? 's' : ''})` : ''} for Review
          </Button>
        </DialogActions>
      </Dialog>

      {/* ─── Cancel Submission Dialog ──────────────────────────────────── */}
      <Dialog
        open={cancelDialogOpen}
        onClose={() => setCancelDialogOpen(false)}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>Cancel Submission</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to cancel this pending submission? This action cannot be undone.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCancelDialogOpen(false)} disabled={cancelSaving}>
            Keep
          </Button>
          <Button
            variant="contained"
            color="error"
            onClick={handleCancelSubmissionConfirm}
            disabled={cancelSaving}
            startIcon={cancelSaving ? <CircularProgress size={16} /> : null}
          >
            Cancel Submission
          </Button>
        </DialogActions>
      </Dialog>

      {/* ─── Submission Confirmation Dialog ────────────────────────────── */}
      <Dialog
        open={!!confirmationDialog}
        onClose={() => setConfirmationDialog(null)}
        maxWidth="xs"
        fullWidth
      >
        <DialogContent sx={{ textAlign: 'center', py: 4 }}>
          <CheckCircleOutlineIcon sx={{ fontSize: 56, color: 'success.main', mb: 2 }} />
          <Typography variant="h6" gutterBottom>
            Submission Received
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Your ZIP changes for <strong>{confirmationDialog?.territory} / {confirmationDialog?.vertical}</strong> have
            been submitted for review.
          </Typography>
          {confirmationDialog?.submissionCount > 1 && (
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 2 }}>
              Created {confirmationDialog.submissionCount} submissions (one per vertical), grouped as one batch.
            </Typography>
          )}
          <Stack direction="row" spacing={2} justifyContent="center">
            {confirmationDialog?.addCount > 0 && (
              <Chip
                label={`${confirmationDialog.addCount} ZIP${confirmationDialog.addCount !== 1 ? 's' : ''} to add`}
                color="success"
                variant="outlined"
              />
            )}
            {confirmationDialog?.removeCount > 0 && (
              <Chip
                label={`${confirmationDialog.removeCount} ZIP${confirmationDialog.removeCount !== 1 ? 's' : ''} to remove`}
                color="error"
                variant="outlined"
              />
            )}
          </Stack>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
            You will be notified once the changes are reviewed.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ justifyContent: 'center', pb: 2 }}>
          <Button variant="contained" onClick={() => setConfirmationDialog(null)}>
            Done
          </Button>
        </DialogActions>
      </Dialog>

      {/* ─── Add Vertical Dialog ─────────────────────────────────────── */}
      <Dialog
        open={addVerticalDialogOpen}
        onClose={() => setAddVerticalDialogOpen(false)}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>Add Verticals</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Select verticals you want to manage ZIPs for. They will appear in the
            territory dropdowns below.
          </Typography>
          <Stack spacing={1} sx={{ maxHeight: 400, overflow: 'auto' }}>
            {allVerticals.map((v) => {
              const alreadyActive = verticals.some((av) => av.id === v.id);
              const isAdded = addedVerticalIds.has(v.id);
              return (
                <Box
                  key={v.id}
                  display="flex"
                  alignItems="center"
                  justifyContent="space-between"
                  sx={{
                    px: 1.5,
                    py: 0.75,
                    borderRadius: 1,
                    bgcolor: alreadyActive || isAdded ? 'action.selected' : 'transparent',
                  }}
                >
                  <Typography variant="body2">{v.vertical}</Typography>
                  {alreadyActive ? (
                    <Chip label="Active" size="small" color="success" variant="outlined" />
                  ) : (
                    <Switch
                      size="small"
                      checked={isAdded}
                      onChange={() => {
                        setAddedVerticalIds((prev) => {
                          const next = new Set(prev);
                          if (next.has(v.id)) {
                            next.delete(v.id);
                          } else {
                            next.add(v.id);
                          }
                          return next;
                        });
                      }}
                    />
                  )}
                </Box>
              );
            })}
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setAddVerticalDialogOpen(false)}>Done</Button>
        </DialogActions>
      </Dialog>

      {/* ─── Snackbar ─────────────────────────────────────────────────── */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={5000}
        onClose={() => setSnackbar((s) => ({ ...s, open: false }))}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          severity={snackbar.severity}
          onClose={() => setSnackbar((s) => ({ ...s, open: false }))}
          variant="filled"
        >
          {snackbar.message}
        </Alert>
      </Snackbar>

      {/* ─── Pause Territory Confirmation ─────────────────────────────── */}
      <Dialog open={pauseDialogOpen} onClose={() => { setPauseDialogOpen(false); setPauseTarget(null); }} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <PauseCircleOutlineIcon color="warning" />
          Pause {pauseTarget?.vertical ? 'Vertical' : 'Territory'}
        </DialogTitle>
        <DialogContent>
          {pauseTarget && (
            <>
              <Typography variant="body1" gutterBottom>
                Are you sure you want to submit a pause request for{' '}
                <strong>
                  {pauseTarget.vertical
                    ? `${pauseTarget.territory.name} / ${pauseTarget.vertical.vertical}`
                    : pauseTarget.territory.name}
                </strong>?
              </Typography>
              <Alert severity="info" sx={{ mt: 1.5 }}>
                {pauseTarget.vertical
                  ? 'All active ZIPs in this vertical will be submitted for removal. This will go to our team for review.'
                  : 'All active ZIPs across every vertical in this territory will be submitted for removal. This will go to our team for review.'}
              </Alert>
            </>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => { setPauseDialogOpen(false); setPauseTarget(null); }} disabled={pauseSaving}>
            Cancel
          </Button>
          <Button
            variant="contained"
            color="warning"
            onClick={handlePauseConfirm}
            disabled={pauseSaving}
            startIcon={pauseSaving ? <CircularProgress size={16} /> : <PauseCircleOutlineIcon />}
          >
            {pauseSaving ? 'Submitting...' : 'Confirm Pause'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* ─── Welcome Overlay (first-time visitors) ────────────────────── */}
      {welcomeOpen && (
        <WelcomeOverlay onStartTour={handleWelcomeStartTour} onSkip={handleWelcomeSkip} />
      )}

      {/* ─── Walkthrough ──────────────────────────────────────────────── */}
      {walkthroughActive && (
        <WalkthroughOverlay
          stepIndex={walkthroughStep}
          steps={WALKTHROUGH_STEPS}
          stepRefs={walkthroughRefs}
          onNext={nextWalkthroughStep}
          onBack={prevWalkthroughStep}
          onSkip={endWalkthrough}
        />
      )}
    </Box>
  );
}

// ─── Helper Components ─────────────────────────────────────────────

const STAT_ICONS = {
  Territories: MapIcon,
  'Active ZIPs': PlaceIcon,
  Population: PeopleIcon,
  Verticals: LayersIcon,
  Pending: PendingActionsIcon,
};

const STAT_COLORS = {
  Territories: '#1976d2',
  'Active ZIPs': '#2e7d32',
  Population: '#7b1fa2',
  Verticals: '#0288d1',
  Pending: '#ed6c02',
};

function StatCard({ label, value, subtitle = null, highlight = false }) {
  const IconComp = STAT_ICONS[label];
  const accent = STAT_COLORS[label] || '#666';
  return (
    <Card
      variant="outlined"
      sx={{
        minWidth: 130,
        flex: '1 1 0',
        borderTop: `3px solid ${highlight ? '#ed6c02' : accent}`,
        ...(highlight && {
          bgcolor: 'warning.50',
        }),
      }}
    >
      <CardContent sx={{ py: 1.5, px: 2, '&:last-child': { pb: 1.5 } }}>
        <Stack direction="row" spacing={1} alignItems="center" mb={0.5}>
          {IconComp && <IconComp sx={{ fontSize: 18, color: accent }} />}
          <Typography variant="caption" color="text.secondary" fontWeight={500}>
            {label}
          </Typography>
        </Stack>
        <Typography variant="h5" fontWeight={700}>
          {value}
        </Typography>
        {subtitle && (
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.25 }}>
            {subtitle}
          </Typography>
        )}
      </CardContent>
    </Card>
  );
}

function StepRow({ number, text }) {
  return (
    <Box display="flex" alignItems="center" gap={1.5} width="100%">
      <Box
        sx={{
          width: 28,
          height: 28,
          borderRadius: '50%',
          bgcolor: 'primary.main',
          color: 'primary.contrastText',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          fontSize: '0.8rem',
          fontWeight: 700,
          flexShrink: 0,
        }}
      >
        {number}
      </Box>
      <Typography variant="body2" textAlign="left">
        {text}
      </Typography>
    </Box>
  );
}
